import { formatMoney } from '@theme/money-formatting';
import { calculateNordanBoxRequirement } from './nordan-box-calculator-core.js';

const NORDAN_PRODUCT_SELECT_EVENT = 'shopify:product:select';

class NordanPdpGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-nordan-gallery-track]');
    this.slides = Array.from(this.querySelectorAll('[data-nordan-gallery-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-nordan-gallery-thumb]'));
    this.openButtons = Array.from(this.querySelectorAll('[data-nordan-gallery-open]'));
    this.prevButton = this.querySelector('[data-nordan-gallery-prev]');
    this.nextButton = this.querySelector('[data-nordan-gallery-next]');
    this.lightbox = this.querySelector('[data-nordan-lightbox]');
    this.lightboxTrack = this.querySelector('[data-nordan-lightbox-track]');
    this.lightboxSlides = Array.from(this.querySelectorAll('[data-nordan-lightbox-slide]'));
    this.lightboxPrevButton = this.querySelector('[data-nordan-lightbox-prev]');
    this.lightboxNextButton = this.querySelector('[data-nordan-lightbox-next]');
    this.lightboxCloseButtons = Array.from(this.querySelectorAll('[data-nordan-lightbox-close]'));
    this.lightboxSwipeArea = this.querySelector('[data-nordan-lightbox-swipe]');
    this.aiDisclosure = this.querySelector('[data-nordan-ai-disclosure]');
    this.aiDisclosureOpenButtons = Array.from(this.querySelectorAll('[data-nordan-ai-disclosure-open]'));
    this.aiDisclosureCloseButtons = Array.from(this.querySelectorAll('[data-nordan-ai-disclosure-close]'));
    this.currentIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
    this.lightboxIndex = this.currentIndex;
    this.desktopLayout = this.dataset.desktopLayout || 'grid';
    this.lightboxEnabled = this.dataset.lightboxEnabled === 'true';
    this.mobileQuery = window.matchMedia('(max-width: 989px)');
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.section = this.closest('.shopify-section') || this;
    this.boundHandleProductSelect = this.handleProductSelect.bind(this);
    this.isLightboxOpen = false;
    this.isPointerDown = false;
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.dragDeltaX = 0;
    this.activePointerId = null;
    this.pointerStartWasProtected = false;
    this.isAiDisclosureOpen = false;
    this.aiDisclosureTrigger = null;

    if (this.currentIndex < 0) {
      this.currentIndex = 0;
    }

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        this.show(Number(thumb.dataset.index));
      });
    });

    this.openButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (this.lightboxEnabled) {
          this.openLightbox(Number(button.dataset.index));
        }
      });
    });

    this.prevButton?.addEventListener('click', () => {
      this.show(this.currentIndex - 1);
    });

    this.nextButton?.addEventListener('click', () => {
      this.show(this.currentIndex + 1);
    });

    this.lightboxPrevButton?.addEventListener('click', () => {
      this.showLightbox(this.lightboxIndex - 1);
    });

    this.lightboxNextButton?.addEventListener('click', () => {
      this.showLightbox(this.lightboxIndex + 1);
    });

    this.lightboxCloseButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.closeLightbox();
      });
    });

    this.aiDisclosureOpenButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.openAiDisclosure(button);
      });
    });

    this.aiDisclosureCloseButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.closeAiDisclosure();
      });
    });

    this.lightboxSwipeArea?.addEventListener('pointerdown', (event) => this.handleLightboxPointerDown(event));
    this.lightboxSwipeArea?.addEventListener('pointermove', (event) => this.handleLightboxPointerMove(event));
    this.lightboxSwipeArea?.addEventListener('pointerup', (event) => this.handleLightboxPointerEnd(event));
    this.lightboxSwipeArea?.addEventListener('pointercancel', (event) => this.handleLightboxPointerEnd(event));
    this.lightboxSwipeArea?.addEventListener('dragstart', (event) => event.preventDefault());

    this.mobileQuery.addEventListener('change', () => {
      this.update();
    });
    this.section.addEventListener(NORDAN_PRODUCT_SELECT_EVENT, this.boundHandleProductSelect);
    this.bindGallerySwipe();

    this.update(false);
    this.scrollActiveThumbnail(false);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(NORDAN_PRODUCT_SELECT_EVENT, this.boundHandleProductSelect);
    this.gallerySwipeController?.abort();
    this.cancelGallerySwipe();
  }

  bindGallerySwipe() {
    this.cancelGallerySwipe();
    this.gallerySwipeController?.abort();
    this.gallerySwipeController = new AbortController();
    this.gallerySwipe = null;
    this.gallerySwipeClickUntil = 0;
    const options = { signal: this.gallerySwipeController.signal, passive: true };

    this.track?.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'touch') return;
      this.cancelGallerySwipe();
      this.gallerySwipeClickUntil = 0;
      if (!event.isPrimary || !this.mobileQuery.matches || this.slides.length < 2 ||
          !event.target.closest('[data-nordan-gallery-open]')) return;
      const width = this.track.clientWidth || window.innerWidth;
      // Catch an in-flight snap at its rendered position, not its target index.
      const transform = getComputedStyle(this.track).transform;
      const originX = transform && transform !== 'none'
        ? new DOMMatrixReadOnly(transform).m41
        : -this.currentIndex * width;
      this.gallerySwipe = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        index: this.currentIndex,
        axis: null,
        width,
        originX,
      };
      this.track.classList.add('is-touch-dragging');
      this.track.style.transform = `translateX(${originX}px)`;
    }, options);
    this.track?.addEventListener('pointermove', (event) => this.moveGallerySwipe(event), options);
    this.track?.addEventListener('pointerup', (event) => this.endGallerySwipe(event), options);
    this.track?.addEventListener('pointercancel', () => this.cancelGallerySwipe(), options);
    this.track?.addEventListener('lostpointercapture', () => this.cancelGallerySwipe(), options);
    this.mobileQuery.addEventListener('change', () => this.cancelGallerySwipe(), options);
    this.track?.addEventListener('click', (event) => {
      // A swipe must not also activate the image's existing lightbox button.
      if (event.detail === 0 || performance.now() >= this.gallerySwipeClickUntil ||
          !event.target.closest('[data-nordan-gallery-open]')) return;
      this.gallerySwipeClickUntil = 0;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, { signal: this.gallerySwipeController.signal, capture: true });
  }

  moveGallerySwipe(event) {
    const gesture = this.gallerySwipe;
    if (!gesture || event.pointerId !== gesture.pointerId) return;
    const x = Math.abs(event.clientX - gesture.startX);
    const y = Math.abs(event.clientY - gesture.startY);
    if (!gesture.axis && Math.max(x, y) >= 10) {
      // Lock direction once; vertical scrolling and pinch zoom remain native.
      gesture.axis = x > y * 1.2 ? 'x' : 'y';
    }
    if (gesture.axis === 'y') {
      this.cancelGallerySwipe();
      return;
    }
    if (gesture.axis !== 'x') return;
    const deltaX = Math.max(-gesture.width, Math.min(event.clientX - gesture.startX, gesture.width));
    let position = gesture.originX + deltaX;
    const lastPosition = -(this.slides.length - 1) * gesture.width;
    // Resist at either end without wrapping or exposing an empty slide.
    if (position > 0) position = Math.min(position * 0.25, gesture.width * 0.15);
    if (position < lastPosition) position = lastPosition - Math.min((lastPosition - position) * 0.25, gesture.width * 0.15);
    this.track.style.transform = `translateX(${position}px)`;
  }

  cancelGallerySwipe() {
    const gesture = this.gallerySwipe;
    if (!gesture) return;
    this.gallerySwipe = null;
    if (gesture.axis === 'x') this.gallerySwipeClickUntil = performance.now() + 500;
    // Commit the last dragged frame before restoring the existing snap transition.
    this.track.getBoundingClientRect();
    this.track.classList.remove('is-touch-dragging');
    const shouldSlide = this.mobileQuery.matches || this.desktopLayout === 'slider';
    this.track.style.transform = shouldSlide ? `translateX(-${this.currentIndex * 100}%)` : '';
  }

  endGallerySwipe(event) {
    const gesture = this.gallerySwipe;
    if (!gesture || event.pointerId !== gesture.pointerId) return;
    this.moveGallerySwipe(event);
    this.cancelGallerySwipe();
    if (!this.mobileQuery.matches || gesture.axis !== 'x') return;
    this.gallerySwipeClickUntil = performance.now() + 500;
    if (this.currentIndex !== gesture.index) return;
    const deltaX = event.clientX - gesture.startX;
    const threshold = Math.max(40, gesture.width * 0.12);
    if (Math.abs(deltaX) >= threshold) {
      this.show(this.currentIndex + (deltaX < 0 ? 1 : -1));
    }
  }

  show(index) {
    if (this.slides.length === 0) return;

    const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    this.currentIndex = nextIndex;
    this.update();
  }

  showVariantMedia(variant) {
    const featuredMediaId = variant?.featured_media?.id;
    const featuredImageId = variant?.featured_image?.id;
    if (!featuredMediaId && !featuredImageId) return;

    const nextIndex = this.slides.findIndex((slide) => {
      return (
        (featuredMediaId && slide.dataset.mediaId === String(featuredMediaId)) ||
        (featuredImageId && slide.dataset.imageId === String(featuredImageId))
      );
    });
    if (nextIndex < 0) return;

    this.show(nextIndex);
    this.lightboxIndex = nextIndex;
    this.updateLightbox(false);
  }

  handleProductSelect(event) {
    if (!(event.target instanceof Element) || !this.section.contains(event.target) || !event.promise) return;
    this.cancelGallerySwipe();

    event.promise
      .then(({ detail }) => this.showVariantMedia(detail?.resource))
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[nordan-gallery] Could not update variant media.', error);
        }
      });
  }

  update(scrollActiveThumb = true) {
    this.cancelGallerySwipe();
    const shouldSlide = this.mobileQuery.matches || this.desktopLayout === 'slider';

    this.slides.forEach((slide, slideIndex) => {
      slide.classList.toggle('is-active', slideIndex === this.currentIndex);
    });

    this.thumbs.forEach((thumb, thumbIndex) => {
      const isActive = thumbIndex === this.currentIndex;
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');

      if (isActive && scrollActiveThumb) {
        this.scrollActiveThumbnail();
      }
    });

    if (this.track) {
      this.track.style.transform = shouldSlide ? `translateX(-${this.currentIndex * 100}%)` : '';
    }

    if (this.prevButton) {
      this.prevButton.hidden = !shouldSlide || this.currentIndex === 0;
    }

    if (this.nextButton) {
      this.nextButton.hidden = !shouldSlide || this.currentIndex === this.slides.length - 1;
    }
  }

  scrollActiveThumbnail(animate = true) {
    const thumb = this.thumbs[this.currentIndex];
    const container = thumb?.closest('.nordan-gallery__thumbs');
    if (!container || !container.clientWidth) return;
    const style = getComputedStyle(container);
    if (style.display !== 'flex') return;
    const behavior = animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto';
    const rect = thumb.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Keep a vertical desktop rail's existing nearest-edge visibility without scrolling its ancestors.
    if (style.flexDirection === 'column') {
      if (!['auto', 'scroll'].includes(style.overflowY)) return;
      const top = rect.top - containerRect.top - container.clientTop;
      const bottom = top + rect.height;
      const delta = top < 0 ? top : bottom > container.clientHeight ? bottom - container.clientHeight : 0;
      if (delta) {
        container.scrollTo({
          top: Math.max(0, Math.min(container.scrollTop + delta, container.scrollHeight - container.clientHeight)),
          behavior,
        });
      }
      return;
    }

    if (style.flexDirection !== 'row' || !['auto', 'scroll'].includes(style.overflowX)) return;
    const maxLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    const previous = this.thumbs[this.currentIndex - 1]?.getBoundingClientRect();
    // Leave one third of the previous thumbnail plus the actual gap visible.
    const peek = previous ? previous.width / 3 + Math.max(0, rect.left - previous.right) : 0;
    const target = this.currentIndex === 0 ? 0 : rect.left - containerRect.left - container.clientLeft + container.scrollLeft - peek;
    container.scrollTo({ left: Math.max(0, Math.min(target, maxLeft)), behavior });
  }

  openLightbox(index) {
    if (!this.lightbox || this.lightboxSlides.length === 0) return;

    this.lightbox.hidden = false;
    this.isLightboxOpen = true;
    document.documentElement.classList.add('nordan-lightbox-open');
    document.addEventListener('keydown', this.boundHandleKeydown);
    this.lightboxIndex = Math.max(0, Math.min(index, this.lightboxSlides.length - 1));
    this.updateLightbox(false);
  }

  closeLightbox() {
    if (!this.lightbox) return;

    this.lightbox.hidden = true;
    this.isLightboxOpen = false;
    this.resetDragState();
    document.documentElement.classList.remove('nordan-lightbox-open');
    document.removeEventListener('keydown', this.boundHandleKeydown);
  }

  openAiDisclosure(trigger) {
    if (!this.aiDisclosure || this.isAiDisclosureOpen) return;

    this.aiDisclosureTrigger = trigger;
    this.aiDisclosure.hidden = false;
    this.aiDisclosure.setAttribute('aria-hidden', 'false');
    this.isAiDisclosureOpen = true;
    document.documentElement.classList.add('nordan-ai-disclosure-open');
    document.addEventListener('keydown', this.boundHandleKeydown);
    this.aiDisclosure.querySelector('.nordan-ai-disclosure__close')?.focus({ preventScroll: true });
  }

  closeAiDisclosure(restoreFocus = true) {
    if (!this.aiDisclosure || !this.isAiDisclosureOpen) return;

    this.aiDisclosure.hidden = true;
    this.aiDisclosure.setAttribute('aria-hidden', 'true');
    this.isAiDisclosureOpen = false;
    document.documentElement.classList.remove('nordan-ai-disclosure-open');
    document.removeEventListener('keydown', this.boundHandleKeydown);

    if (restoreFocus) {
      this.aiDisclosureTrigger?.focus({ preventScroll: true });
    }

    this.aiDisclosureTrigger = null;
  }

  showLightbox(index, animate = true) {
    if (this.lightboxSlides.length === 0) return;

    this.lightboxIndex = Math.max(0, Math.min(index, this.lightboxSlides.length - 1));
    this.updateLightbox(animate);
  }

  updateLightbox(animate = true, offsetPx = 0) {
    this.lightboxSlides.forEach((slide, slideIndex) => {
      slide.classList.toggle('is-active', slideIndex === this.lightboxIndex);
      slide.setAttribute('aria-hidden', slideIndex === this.lightboxIndex ? 'false' : 'true');
    });

    if (this.lightboxTrack) {
      this.lightboxTrack.classList.toggle('is-dragging', !animate);
      this.lightboxTrack.style.transform = `translateX(calc(-${this.lightboxIndex * 100}% + ${offsetPx}px))`;
    }

    this.syncLightboxControls();
  }

  syncLightboxControls() {
    this.setLightboxControlState(this.lightboxPrevButton, this.lightboxIndex === 0);
    this.setLightboxControlState(this.lightboxNextButton, this.lightboxIndex === this.lightboxSlides.length - 1);
  }

  setLightboxControlState(button, isHidden) {
    if (!button) return;

    button.classList.toggle('is-hidden', isHidden);
    button.setAttribute('aria-disabled', isHidden ? 'true' : 'false');
    button.tabIndex = isHidden ? -1 : 0;
  }

  handleLightboxPointerDown(event) {
    if (!this.isLightboxOpen || event.target.closest('button')) return;

    this.isPointerDown = true;
    this.isDragging = false;
    this.activePointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.dragDeltaX = 0;
    this.pointerStartWasProtected = this.isLightboxPointerProtected(event);
    this.lightboxSwipeArea.setPointerCapture?.(event.pointerId);
  }

  handleLightboxPointerMove(event) {
    if (!this.isPointerDown || event.pointerId !== this.activePointerId) return;

    const deltaX = event.clientX - this.dragStartX;
    const deltaY = event.clientY - this.dragStartY;

    if (!this.isDragging && Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
    if (!this.isDragging && Math.abs(deltaY) > Math.abs(deltaX)) return;

    event.preventDefault();
    this.isDragging = true;
    this.dragDeltaX = this.applyEdgeResistance(deltaX);
    this.updateLightbox(false, this.dragDeltaX);
  }

  handleLightboxPointerEnd(event) {
    if (!this.isPointerDown || event.pointerId !== this.activePointerId) return;

    this.lightboxSwipeArea.releasePointerCapture?.(event.pointerId);

    const viewportWidth = this.lightboxSwipeArea?.clientWidth || window.innerWidth;
    const threshold = Math.max(this.mobileQuery.matches ? 40 : 50, viewportWidth * 0.15);
    const deltaX = this.dragDeltaX;
    const totalDeltaX = event.clientX - this.dragStartX;
    const totalDeltaY = event.clientY - this.dragStartY;
    const movedDistance = Math.hypot(totalDeltaX, totalDeltaY);
    const wasDragging = this.isDragging || movedDistance > 10;
    const startedOnProtectedArea = this.pointerStartWasProtected;
    const endedOnProtectedArea = this.isLightboxPointerProtected(event);

    this.resetDragState();

    if (Math.abs(deltaX) >= threshold) {
      if (deltaX < 0) {
        this.showLightbox(this.lightboxIndex + 1);
      } else {
        this.showLightbox(this.lightboxIndex - 1);
      }
    } else if (!wasDragging && !startedOnProtectedArea && !endedOnProtectedArea) {
      this.closeLightbox();
    } else {
      this.updateLightbox(true);
    }
  }

  isLightboxPointerProtected(event) {
    return this.isLightboxButton(event.target) || this.isPointerInsideActiveImage(event);
  }

  isLightboxButton(target) {
    return Boolean(target?.closest?.('.nordan-lightbox__control, [data-nordan-lightbox-close], [data-nordan-lightbox-prev], [data-nordan-lightbox-next]'));
  }

  isPointerInsideActiveImage(event) {
    const image = this.lightboxSlides[this.lightboxIndex]?.querySelector('.nordan-lightbox__image');
    if (!image) return false;

    const imageRect = image.getBoundingClientRect();
    const naturalWidth = image.naturalWidth || image.videoWidth || imageRect.width;
    const naturalHeight = image.naturalHeight || image.videoHeight || imageRect.height;

    if (!naturalWidth || !naturalHeight || !imageRect.width || !imageRect.height) {
      return event.clientX >= imageRect.left && event.clientX <= imageRect.right && event.clientY >= imageRect.top && event.clientY <= imageRect.bottom;
    }

    const imageRatio = naturalWidth / naturalHeight;
    const boxRatio = imageRect.width / imageRect.height;
    let renderedWidth = imageRect.width;
    let renderedHeight = imageRect.height;

    if (imageRatio > boxRatio) {
      renderedHeight = renderedWidth / imageRatio;
    } else {
      renderedWidth = renderedHeight * imageRatio;
    }

    const renderedLeft = imageRect.left + (imageRect.width - renderedWidth) / 2;
    const renderedTop = imageRect.top + (imageRect.height - renderedHeight) / 2;
    const renderedRight = renderedLeft + renderedWidth;
    const renderedBottom = renderedTop + renderedHeight;

    return event.clientX >= renderedLeft && event.clientX <= renderedRight && event.clientY >= renderedTop && event.clientY <= renderedBottom;
  }

  applyEdgeResistance(deltaX) {
    const atFirst = this.lightboxIndex === 0;
    const atLast = this.lightboxIndex === this.lightboxSlides.length - 1;

    if (atFirst && deltaX > 0) {
      return deltaX * 0.28;
    }

    if (atLast && deltaX < 0) {
      return deltaX * 0.28;
    }

    return deltaX;
  }

  resetDragState() {
    this.isPointerDown = false;
    this.isDragging = false;
    this.activePointerId = null;
    this.dragDeltaX = 0;
    this.pointerStartWasProtected = false;
  }

  handleKeydown(event) {
    if (this.isAiDisclosureOpen) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeAiDisclosure();
      }
      return;
    }

    if (!this.isLightboxOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeLightbox();
      return;
    }

    if (event.key === 'ArrowLeft') {
      this.showLightbox(this.lightboxIndex - 1);
    }

    if (event.key === 'ArrowRight') {
      this.showLightbox(this.lightboxIndex + 1);
    }
  }
}

if (!customElements.get('nordan-pdp-gallery')) {
  customElements.define('nordan-pdp-gallery', NordanPdpGallery);
}

const NORDAN_COMPARISON_DATA_NOTE_SELECTOR =
  '.product-information__grid:has(.nordan-gallery) .product-details .nordan-comparison-table__data-note';

function closeNordanComparisonDataNote(dataNote, restoreFocus = false) {
  if (!(dataNote instanceof HTMLDetailsElement) || !dataNote.open) return;

  const activeElement = document.activeElement;
  const shouldRestoreFocus = restoreFocus && activeElement instanceof Node && dataNote.contains(activeElement);
  dataNote.open = false;

  if (shouldRestoreFocus) {
    dataNote.querySelector('summary')?.focus();
  }
}

function handleNordanComparisonDataNoteClick(event) {
  const target = event.target;
  if (!(target instanceof Node)) return;

  document.querySelectorAll(`${NORDAN_COMPARISON_DATA_NOTE_SELECTOR}[open]`).forEach((dataNote) => {
    if (!dataNote.contains(target)) closeNordanComparisonDataNote(dataNote);
  });
}

function handleNordanComparisonDataNoteKeydown(event) {
  if (event.key !== 'Escape') return;

  document.querySelectorAll(`${NORDAN_COMPARISON_DATA_NOTE_SELECTOR}[open]`).forEach((dataNote) => {
    closeNordanComparisonDataNote(dataNote, true);
  });
}

document.addEventListener('click', handleNordanComparisonDataNoteClick);
document.addEventListener('keydown', handleNordanComparisonDataNoteKeydown);

const NORDAN_UNIT_PRICE_GRID_SELECTOR = '.product-information__grid:has(.nordan-gallery)';
const NORDAN_UNIT_PRICE_SELECTOR = '.product-details product-price';
const NORDAN_COLOR_SWATCH_SELECTOR = '.product-information__grid:has(.nordan-gallery) .product-details .nordan-color-swatches input[type="radio"]';
const NORDAN_STICKY_DETAILS_SELECTOR = `${NORDAN_UNIT_PRICE_GRID_SELECTOR} .product-details.sticky-content--desktop`;
const NORDAN_STICKY_DETAILS_TOP_PROPERTY = '--nordan-product-details-sticky-top';
const NORDAN_STICKY_DETAILS_BREAKPOINT = window.matchMedia('(min-width: 750px)');
const NORDAN_STICKY_DETAILS_BOTTOM_GAP = 16;
const nordanStickyDetailsByElement = new WeakMap();
const nordanStickyDetailsInstances = new Set();

class NordanStickyProductDetails {
  constructor(details) {
    this.details = details;
    this.section = details.closest('.shopify-section') || details;
    this.abortController = new AbortController();
    this.lastObservedHeight = null;
    this.resizeObserver = new ResizeObserver(([entry]) => {
      const observedHeight = entry?.contentRect.height;
      if (observedHeight === this.lastObservedHeight) return;

      this.lastObservedHeight = observedHeight;
      this.scheduleMeasure();
    });
    this.measureFrame = null;
    this.isDestroyed = false;
    this.baseTop = null;
    this.boundHandleResize = this.handleResize.bind(this);

    this.resizeObserver.observe(this.details);
    window.addEventListener('resize', this.boundHandleResize, { signal: this.abortController.signal });
    NORDAN_STICKY_DETAILS_BREAKPOINT.addEventListener('change', this.boundHandleResize, { signal: this.abortController.signal });
    this.scheduleMeasure();
  }

  readBaseTop() {
    const computedTop = Number.parseFloat(window.getComputedStyle(this.details).top);
    return Number.isFinite(computedTop) ? computedTop : 0;
  }

  handleResize() {
    this.baseTop = null;
    this.updateStickyTop(null);
    this.scheduleMeasure();
  }

  updateStickyTop(stickyTop) {
    const currentValue = this.details.style.getPropertyValue(NORDAN_STICKY_DETAILS_TOP_PROPERTY);
    const nextValue = Number.isFinite(stickyTop) ? `${stickyTop}px` : '';

    if (currentValue === nextValue) return;

    if (nextValue) {
      this.details.style.setProperty(NORDAN_STICKY_DETAILS_TOP_PROPERTY, nextValue);
    } else {
      this.details.style.removeProperty(NORDAN_STICKY_DETAILS_TOP_PROPERTY);
    }
  }

  scheduleMeasure() {
    if (this.isDestroyed || this.measureFrame !== null) return;

    this.measureFrame = requestAnimationFrame(() => {
      this.measureFrame = null;
      this.measure();
    });
  }

  measure() {
    if (this.isDestroyed) return;

    if (!this.details?.isConnected) {
      this.destroy();
      return;
    }

    if (!NORDAN_STICKY_DETAILS_BREAKPOINT.matches || !this.details.classList.contains('sticky-content--desktop')) {
      this.baseTop = null;
      this.updateStickyTop(null);
      return;
    }

    const baseTop = this.baseTop ?? this.readBaseTop();
    const detailsHeight = Math.max(this.details.getBoundingClientRect().height, this.details.scrollHeight);
    const availableHeight = window.innerHeight - baseTop - NORDAN_STICKY_DETAILS_BOTTOM_GAP;
    const stickyTop =
      detailsHeight > availableHeight
        ? Math.min(baseTop, window.innerHeight - detailsHeight - NORDAN_STICKY_DETAILS_BOTTOM_GAP)
        : null;

    this.baseTop = baseTop;
    this.updateStickyTop(stickyTop);
  }

  destroy() {
    if (this.isDestroyed) return;

    this.isDestroyed = true;
    this.abortController.abort();
    this.resizeObserver.disconnect();

    if (this.measureFrame !== null) {
      cancelAnimationFrame(this.measureFrame);
      this.measureFrame = null;
    }

    this.details?.style.removeProperty(NORDAN_STICKY_DETAILS_TOP_PROPERTY);
    nordanStickyDetailsByElement.delete(this.details);
    nordanStickyDetailsInstances.delete(this);
    this.abortController = null;
    this.resizeObserver = null;
    this.section = null;
    this.details = null;
  }
}

function initNordanStickyProductDetails() {
  document.querySelectorAll(NORDAN_STICKY_DETAILS_SELECTOR).forEach((details) => {
    if (!(details instanceof HTMLElement) || nordanStickyDetailsByElement.has(details)) return;

    const instance = new NordanStickyProductDetails(details);
    nordanStickyDetailsByElement.set(details, instance);
    nordanStickyDetailsInstances.add(instance);
  });
}

function destroyNordanStickyProductDetails(event) {
  const sectionRoot = event.target instanceof Element ? event.target : null;
  const sectionId = event.detail?.sectionId;

  nordanStickyDetailsInstances.forEach((instance) => {
    const belongsToSection =
      !instance.details?.isConnected ||
      sectionRoot?.contains(instance.details) ||
      (sectionId && instance.section?.id === `shopify-section-${sectionId}`);

    if (belongsToSection) instance.destroy();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNordanStickyProductDetails);
} else {
  initNordanStickyProductDetails();
}

document.addEventListener('shopify:section:load', initNordanStickyProductDetails);
document.addEventListener('shopify:section:unload', destroyNordanStickyProductDetails);

function updateNordanColorHeading(input) {
  if (!(input instanceof HTMLInputElement)) return;

  const fieldset = input.closest('.nordan-color-swatches');
  const headingValue = fieldset?.querySelector('.nordan-color-swatches__heading-value');
  const legend = fieldset?.querySelector('legend.visually-hidden');
  const headingLabel = fieldset?.querySelector('.nordan-color-swatches__heading-label')?.textContent?.trim() || 'Select Color:';

  if (headingValue) {
    headingValue.textContent = input.value;
  }

  if (legend) {
    legend.textContent = `${headingLabel.replace(/:\s*$/, '')}: ${input.value}`;
  }
}

function handleNordanColorSwatchChange(event) {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.matches(NORDAN_COLOR_SWATCH_SELECTOR)) return;

  updateNordanColorHeading(input);
}

function handleNordanColorProductSelect(event) {
  const variantPicker = event.target;
  if (!(variantPicker instanceof Element) || !variantPicker.matches('variant-picker') || !event.promise) return;

  event.promise.catch((error) => {
    if (error?.name === 'AbortError') return;

    const selectedColor = variantPicker.querySelector('.nordan-color-swatches input[type="radio"]:checked');
    updateNordanColorHeading(selectedColor);
  });
}

document.addEventListener('change', handleNordanColorSwatchChange);
document.addEventListener(NORDAN_PRODUCT_SELECT_EVENT, handleNordanColorProductSelect);

class NordanUnitPrice {
  constructor(grid) {
    this.grid = grid;
    this.section = grid.closest('.shopify-section') || grid;
    this.price = grid.querySelector(NORDAN_UNIT_PRICE_SELECTOR);
    this.variantPicker = grid.querySelector('.product-details variant-picker');
    this.config = this.getConfig();
    this.warnedMissingCoverage = new Set();
    this.isReady = Boolean(this.price && this.variantPicker && this.config?.enabled);

    if (!this.isReady) return;

    this.node = this.createNode();
    this.boundHandleProductSelect = this.handleProductSelect.bind(this);
    this.section.addEventListener(NORDAN_PRODUCT_SELECT_EVENT, this.boundHandleProductSelect);
    this.section.addEventListener('nordan:price-preview', (event) => this.handlePricePreview(event));
    this.update(this.getCurrentVariant());
  }

  getConfig() {
    const configScript = this.section.querySelector('[data-nordan-unit-price-config]');
    if (!configScript?.textContent) return null;

    try {
      const config = JSON.parse(configScript.textContent);
      return {
        enabled: config.enabled !== false,
        moneyFormat: config.moneyFormat || '${{amount}}',
        currency: config.currency || 'USD',
        variants: config.variants || {},
      };
    } catch (error) {
      console.warn('[nordan-unit-price] Invalid unit price config.', error);
      return null;
    }
  }

  createNode() {
    const node = document.createElement('span');
    node.className = 'nordan-unit-price-inline';
    node.dataset.nordanUnitPriceInline = '';
    node.hidden = true;
    return node;
  }

  getCurrentVariant() {
    const variantScript = this.variantPicker?.querySelector('script[type="application/json"]');
    if (!variantScript?.textContent) return null;

    try {
      return JSON.parse(variantScript.textContent);
    } catch (error) {
      console.warn('[nordan-unit-price] Invalid selected variant JSON.', error);
      return null;
    }
  }

  getVisiblePriceTarget() {
    return (
      this.price?.querySelector('[ref="priceContainer"] .price__sale:not(.price__hidden) .price-item--sale.price') ||
      this.price?.querySelector('[ref="priceContainer"] .price__regular:not(.price__hidden) .price') ||
      this.price?.querySelector('[ref="priceContainer"] .price') ||
      null
    );
  }

  ensureInlineNode() {
    const target = this.getVisiblePriceTarget();
    if (!target || !this.node) return false;

    if (this.node.previousElementSibling !== target) {
      target.insertAdjacentElement('afterend', this.node);
    }

    return true;
  }

  handleProductSelect(event) {
    if (!(event.target instanceof Element) || !this.grid.contains(event.target) || !event.promise) return;

    event.promise
      .then(({ detail }) => {
        if (!detail || (this.section.nordanPricePreview && !this.section.nordanPricePreview.isCurrentEvent(event))) return;
        this.pricePreviewSnapshot = null;
        if (detail.productId && this.price?.dataset.productId && String(detail.productId) !== String(this.price.dataset.productId)) return;

        this.update(detail.resource);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError' && (!this.section.nordanPricePreview || this.section.nordanPricePreview.isCurrentEvent(event))) {
          if (!this.section.nordanPricePreview?.hasPreviewed(event)) this.update(this.getCurrentVariant());
          console.warn('[nordan-unit-price] Variant update failed.', error);
        }
      });
  }

  handlePricePreview(event) {
    if (event.detail?.productId !== this.price?.dataset.productId || !this.price.isConnected) return;
    if (event.detail.phase === 'rollback') {
      if (this.pricePreviewSnapshot) {
        this.node.textContent = this.pricePreviewSnapshot.text;
        this.node.hidden = this.pricePreviewSnapshot.hidden;
        this.ensureInlineNode();
      }
      this.pricePreviewSnapshot = null;
      return;
    }
    const preview = this.section.nordanPricePreview?.getPreview(this.price.dataset.productId);
    if (!preview) return;
    this.pricePreviewSnapshot ||= { text: this.node.textContent, hidden: this.node.hidden };
    this.update(preview.variant);
  }

  getCoverage(variant) {
    if (!variant?.id) return null;
    const variantConfig = this.config.variants[String(variant.id)];
    const coverage = Number(variantConfig?.coverageSqFt);

    return Number.isFinite(coverage) && coverage > 0 ? coverage : null;
  }

  update(variant) {
    const preview = this.section.nordanPricePreview?.getPreview(this.price?.dataset.productId);
    if (preview) {
      if (preview.variant.coverageSqFt === null) return;
      variant = preview.variant;
    }
    if (!this.node || variant?.price == null || !this.ensureInlineNode()) {
      this.hide();
      return;
    }

    const coverage = preview ? preview.variant.coverageSqFt : this.getCoverage(variant);
    if (!coverage) {
      const variantId = variant?.id ? String(variant.id) : 'unknown';
      if (!this.warnedMissingCoverage.has(variantId)) {
        console.warn(`[nordan-unit-price] Missing coverage for variant ${variantId}.`);
        this.warnedMissingCoverage.add(variantId);
      }
      this.hide();
      return;
    }

    const unitPriceMinor = Math.round(Number(variant.price) / coverage);
    const unitPrice = formatMoney(unitPriceMinor, preview?.moneyFormat || this.config.moneyFormat, preview?.currency || this.config.currency);

    this.node.textContent = ` (${unitPrice}/ft²)`;
    this.node.hidden = false;
  }

  hide() {
    if (this.node) this.node.hidden = true;
  }
}

function initNordanUnitPrices() {
  document.querySelectorAll(NORDAN_UNIT_PRICE_GRID_SELECTOR).forEach((grid) => {
    if (!(grid instanceof HTMLElement) || grid.dataset.nordanUnitPriceInitialized === 'true') return;

    const unitPrice = new NordanUnitPrice(grid);
    if (unitPrice.isReady) {
      grid.dataset.nordanUnitPriceInitialized = 'true';
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNordanUnitPrices);
} else {
  initNordanUnitPrices();
}

document.addEventListener('shopify:section:load', initNordanUnitPrices);

const NORDAN_QUANTITY_COVERAGE_SELECTOR = '[data-nordan-quantity-coverage]';
const NORDAN_QUANTITY_UPDATE_EVENT = 'quantity-selector:update';
const nordanQuantityCoverageByRoot = new WeakMap();
const nordanQuantityCoverageInstances = new Set();

class NordanQuantityCoverage {
  constructor(root) {
    this.root = root;
    this.grid = root.closest('.product-information__grid');
    if (!this.grid?.querySelector('.nordan-gallery')) this.grid = null;

    this.section = root.closest('.shopify-section') || this.grid || root;
    this.variantPicker =
      this.grid?.querySelector('.product-details variant-picker') ||
      this.section.querySelector('.product-details variant-picker');
    this.output = root.querySelector('[data-nordan-coverage-output]');
    this.valueNode = root.querySelector('[data-nordan-coverage-value]');
    this.quantityInput = root.querySelector('input[name="quantity"]');
    this.config = this.getConfig();
    this.currentCoverage = null;
    this.isReady = Boolean(this.output && this.valueNode && this.quantityInput && this.variantPicker && this.config?.variants);

    if (!this.isReady) {
      this.hideCoverage();
      return;
    }

    this.boundHandleProductSelect = this.handleProductSelect.bind(this);
    this.boundHandleQuantityChange = this.handleQuantityChange.bind(this);
    this.boundHandleVariantOptionChange = this.handleVariantOptionChange.bind(this);
    this.section.addEventListener(NORDAN_PRODUCT_SELECT_EVENT, this.boundHandleProductSelect);
    this.section.addEventListener('change', this.boundHandleVariantOptionChange, true);
    this.root.addEventListener(NORDAN_QUANTITY_UPDATE_EVENT, this.boundHandleQuantityChange);
    this.quantityInput.addEventListener('input', this.boundHandleQuantityChange);
    this.quantityInput.addEventListener('change', this.boundHandleQuantityChange);
    this.updateCoverage(this.getCurrentVariant());
  }

  getConfig() {
    const configScript = this.section.querySelector('[data-nordan-unit-price-config]');
    if (!configScript?.textContent) return null;

    try {
      const config = JSON.parse(configScript.textContent);
      return {
        variants: config.variants || {},
      };
    } catch (error) {
      console.warn('[nordan-quantity-coverage] Invalid coverage config.', error);
      return null;
    }
  }

  getCurrentVariant() {
    const variantScript = this.variantPicker?.querySelector('script[type="application/json"]');
    if (!variantScript?.textContent) return null;

    try {
      return JSON.parse(variantScript.textContent);
    } catch (error) {
      console.warn('[nordan-quantity-coverage] Invalid selected variant JSON.', error);
      return null;
    }
  }

  handleProductSelect(event) {
    const eventRoot = this.grid || this.section;
    if (!(event.target instanceof Element) || !eventRoot.contains(event.target) || !event.promise) return;

    event.promise
      .then(() => {
        requestAnimationFrame(initNordanQuantityCoverages);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          requestAnimationFrame(initNordanQuantityCoverages);
          console.warn('[nordan-quantity-coverage] Variant update failed.', error);
        }
      });
  }

  handleVariantOptionChange(event) {
    const target = event.target;
    const eventRoot = this.grid || this.section;
    if (
      !(target instanceof HTMLInputElement) ||
      !eventRoot.contains(target) ||
      !target.matches('.product-details variant-picker input[data-variant-id]')
    ) {
      return;
    }

    this.updateCoverageByVariantId(target.dataset.variantId);
  }

  handleQuantityChange() {
    this.updateOutput();
  }

  getCoverageByVariantId(variantId) {
    if (!variantId) return null;
    const variantConfig = this.config.variants[String(variantId)];
    const coverage = Number(variantConfig?.coverageSqFt);

    return Number.isFinite(coverage) && coverage > 0 ? coverage : null;
  }

  getCoverage(variant) {
    return this.getCoverageByVariantId(variant?.id);
  }

  getQuantity() {
    const quantity = Number(this.quantityInput?.value);

    return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
  }

  updateCoverage(variant) {
    this.currentCoverage = this.getCoverage(variant);
    this.updateOutput();
  }

  updateCoverageByVariantId(variantId) {
    const coverage = this.getCoverageByVariantId(variantId);
    if (!coverage) return;

    this.currentCoverage = coverage;
    this.updateOutput();
  }

  updateOutput() {
    const quantity = this.getQuantity();
    if (!this.currentCoverage || !quantity || !this.valueNode) {
      this.hideCoverage();
      return;
    }

    this.valueNode.textContent = (this.currentCoverage * quantity).toFixed(2);
    this.output.hidden = false;
  }

  hideCoverage() {
    if (this.output) this.output.hidden = true;
  }

  refresh() {
    const variantPicker =
      this.grid?.querySelector('.product-details variant-picker') ||
      this.section.querySelector('.product-details variant-picker');
    if (
      !this.root.isConnected ||
      this.output !== this.root.querySelector('[data-nordan-coverage-output]') ||
      this.valueNode !== this.root.querySelector('[data-nordan-coverage-value]') ||
      this.quantityInput !== this.root.querySelector('input[name="quantity"]') ||
      this.variantPicker !== variantPicker
    ) {
      return false;
    }

    this.config = this.getConfig();
    if (!this.config?.variants) return false;
    this.updateCoverage(this.getCurrentVariant());
    return true;
  }

  destroy() {
    this.section.removeEventListener(NORDAN_PRODUCT_SELECT_EVENT, this.boundHandleProductSelect);
    this.section.removeEventListener('change', this.boundHandleVariantOptionChange, true);
    this.root.removeEventListener(NORDAN_QUANTITY_UPDATE_EVENT, this.boundHandleQuantityChange);
    this.quantityInput.removeEventListener('input', this.boundHandleQuantityChange);
    this.quantityInput.removeEventListener('change', this.boundHandleQuantityChange);
    this.root.removeAttribute('data-nordan-quantity-coverage-initialized');
    nordanQuantityCoverageInstances.delete(this);
    nordanQuantityCoverageByRoot.delete(this.root);
  }
}

function initNordanQuantityCoverages() {
  nordanQuantityCoverageInstances.forEach((quantityCoverage) => {
    if (!quantityCoverage.root.isConnected) quantityCoverage.destroy();
  });

  document.querySelectorAll(NORDAN_QUANTITY_COVERAGE_SELECTOR).forEach((root) => {
    if (!(root instanceof HTMLElement)) return;

    const existingCoverage = nordanQuantityCoverageByRoot.get(root);
    if (existingCoverage?.refresh()) return;
    existingCoverage?.destroy();

    const quantityCoverage = new NordanQuantityCoverage(root);
    if (quantityCoverage.isReady) {
      root.dataset.nordanQuantityCoverageInitialized = 'true';
      nordanQuantityCoverageByRoot.set(root, quantityCoverage);
      nordanQuantityCoverageInstances.add(quantityCoverage);
    }
  });
}

function destroyNordanQuantityCoverages(event) {
  const sectionRoot = event.target instanceof Element ? event.target : null;
  const sectionId = event.detail?.sectionId;
  nordanQuantityCoverageInstances.forEach((quantityCoverage) => {
    if (
      !quantityCoverage.root.isConnected ||
      sectionRoot?.contains(quantityCoverage.root) ||
      (sectionId && quantityCoverage.section.id === `shopify-section-${sectionId}`)
    ) {
      quantityCoverage.destroy();
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNordanQuantityCoverages);
} else {
  initNordanQuantityCoverages();
}

document.addEventListener('shopify:section:load', initNordanQuantityCoverages);
document.addEventListener('shopify:section:unload', destroyNordanQuantityCoverages);

const NORDAN_STICKY_ADD_TO_CART_SELECTOR = 'sticky-add-to-cart.nordan-sticky-add-to-cart';
const NORDAN_STICKY_QUANTITY_DECREASE_SELECTOR = '[data-nordan-sticky-quantity-decrease]';
const NORDAN_STICKY_QUANTITY_INCREASE_SELECTOR = '[data-nordan-sticky-quantity-increase]';
const NORDAN_STICKY_QUANTITY_VALUE_SELECTOR = '[data-nordan-sticky-quantity-value]';
const nordanStickyQuantityByRoot = new WeakMap();
const nordanStickyQuantityInstances = new Set();

class NordanStickyQuantityControls {
  constructor(root) {
    this.root = root;
    this.section = root.closest('.shopify-section');
    this.syncFrame = null;
    this.isDestroyed = false;
    this.boundHandleClick = this.handleClick.bind(this);
    this.boundHandleQuantityUpdate = this.handleQuantityUpdate.bind(this);
    this.boundHandleProductSelect = this.handleProductSelect.bind(this);
    this.boundHandlePricePreview = this.handlePricePreview.bind(this);
    this.boundHandleBundleQuote = this.handleBundleQuote.bind(this);
    this.isReady = this.section instanceof HTMLElement;

    if (!this.isReady) return;

    this.root.addEventListener('click', this.boundHandleClick);
    this.section.addEventListener(NORDAN_QUANTITY_UPDATE_EVENT, this.boundHandleQuantityUpdate);
    this.section.addEventListener(NORDAN_PRODUCT_SELECT_EVENT, this.boundHandleProductSelect);
    this.section.addEventListener('nordan:price-preview', this.boundHandlePricePreview);
    this.section.addEventListener('nordan:bundle-quote', this.boundHandleBundleQuote);
    customElements.whenDefined('quantity-selector-component').then(() => this.scheduleSync());
    this.scheduleSync();
  }

  getQuantityContext() {
    if (!this.root?.isConnected || !this.section?.isConnected) return null;

    const productId = this.root.dataset.productId;
    if (!productId) return null;

    const productForm = this.section.querySelector(`product-form-component[data-product-id="${productId}"]`);
    const quantityInput = productForm?.querySelector('quantity-selector-component input[name="quantity"]');
    const quantitySelector = quantityInput?.closest('quantity-selector-component');

    if (
      !(quantityInput instanceof HTMLInputElement) ||
      !(quantitySelector instanceof HTMLElement) ||
      typeof quantitySelector.getCurrentValues !== 'function' ||
      typeof quantitySelector.getEffectiveMax !== 'function' ||
      typeof quantitySelector.updateQuantity !== 'function'
    ) {
      return null;
    }

    return { productForm, quantityInput, quantitySelector };
  }

  scheduleSync() {
    if (this.isDestroyed || !this.root?.isConnected || !this.section?.isConnected) return;
    if (this.syncFrame !== null) cancelAnimationFrame(this.syncFrame);

    this.syncFrame = requestAnimationFrame(() => {
      this.syncFrame = null;
      this.syncControls();
    });
  }

  syncControls(context = this.getQuantityContext()) {
    const valueNode = this.root?.querySelector(NORDAN_STICKY_QUANTITY_VALUE_SELECTOR);
    const decreaseButton = this.root?.querySelector(NORDAN_STICKY_QUANTITY_DECREASE_SELECTOR);
    const increaseButton = this.root?.querySelector(NORDAN_STICKY_QUANTITY_INCREASE_SELECTOR);

    if (
      !(valueNode instanceof HTMLElement) ||
      !(decreaseButton instanceof HTMLButtonElement) ||
      !(increaseButton instanceof HTMLButtonElement)
    ) {
      return;
    }

    if (!context) {
      decreaseButton.disabled = true;
      increaseButton.disabled = true;
      return;
    }

    const { min, value } = context.quantitySelector.getCurrentValues();
    const effectiveMax = context.quantitySelector.getEffectiveMax();
    const canUpdate = this.root.dataset.variantAvailable === 'true' && !context.quantityInput.disabled;

    this.syncPrices(context, value);
    valueNode.textContent = value.toString();
    decreaseButton.disabled = !canUpdate || value <= min;
    increaseButton.disabled = !canUpdate || (effectiveMax !== null && value >= effectiveMax);
  }

  syncPrices(context, quantity) {
    this.observeBundlePrices(context.productForm.closest('.product-details'));
    if (this.syncBundlePrice(context, quantity)) return;
    const preview = this.section.nordanPricePreview?.getPreview(this.root.dataset.productId);
    if (preview) {
      const total = preview.variant.price * quantity;
      const compare = preview.variant.compare_at_price;
      if (compare !== null && !Number.isSafeInteger(compare * quantity)) return;
      if (Number.isSafeInteger(quantity) && quantity > 0 && context.quantityInput.validity.valid && Number.isSafeInteger(total)) {
        this.writePreviewPrice(this.previewMoney(total, preview), compare === null ? '' : this.previewMoney(compare * quantity, preview), compare !== null && compare > preview.variant.price);
      }
      return;
    }
    const variantScript = this.section.querySelector('.product-details variant-picker script[type="application/json"]');
    const configScript = this.section.querySelector('[data-nordan-unit-price-config]');
    if (!variantScript?.textContent || !configScript?.textContent) return;

    let variant;
    let config;
    try {
      variant = JSON.parse(variantScript.textContent);
      config = JSON.parse(configScript.textContent);
    } catch {
      return;
    }

    const variantId = context.productForm.querySelector('input[name="id"]')?.value;
    if (!variant?.id || String(variant.id) !== variantId || !Number.isSafeInteger(quantity) || quantity < 1) return;
    if (!Number.isSafeInteger(variant.price) || variant.price < 0 || !config.moneyFormat || !config.currency) return;

    const currentPrice = this.root.querySelector('.nordan-sticky-add-to-cart__button-price-current');
    const comparePrice = this.root.querySelector('.nordan-sticky-add-to-cart__button-price-compare');
    if (currentPrice && Number.isSafeInteger(variant.price * quantity)) {
      currentPrice.textContent = formatMoney(variant.price * quantity, config.moneyFormat, config.currency);
    }
    if (comparePrice && Number.isSafeInteger(variant.compare_at_price) && Number.isSafeInteger(variant.compare_at_price * quantity)) {
      comparePrice.textContent = formatMoney(variant.compare_at_price * quantity, config.moneyFormat, config.currency);
    }
  }

  previewMoney(amount, preview) {
    const template = document.createElement('template');
    template.innerHTML = formatMoney(amount, preview.moneyFormat, preview.currency);
    return template.content.textContent;
  }

  writePreviewPrice(total, original, showCompare) {
    const current = this.root.querySelector('.nordan-sticky-add-to-cart__button-price-current');
    if (!current) return;
    let compare = this.root.querySelector('.nordan-sticky-add-to-cart__button-price-compare');
    if (!compare && showCompare) {
      compare = document.createElement('s');
      compare.className = 'nordan-sticky-add-to-cart__button-price-compare';
      current.parentElement.append(compare);
    }
    if (current.textContent !== total) current.textContent = total;
    if (compare) {
      if (compare.textContent !== original) compare.textContent = original;
      compare.hidden = !showCompare;
    }
  }

  handleBundleQuote(event) {
    if (event.target?.config?.productId !== this.root.dataset.productId) return;
    const context = this.getQuantityContext();
    if (context && event.target.closest('.product-details') === context.productForm.closest('.product-details')) {
      this.syncPrices(context, Number(context.quantityInput.value));
    }
  }

  handlePricePreview(event) {
    if (event.detail?.productId !== this.root.dataset.productId) return;
    if (event.detail.phase === 'rollback') {
      const snapshot = this.pricePreviewSnapshot;
      if (snapshot?.current.isConnected) {
        snapshot.current.textContent = snapshot.text;
        if (snapshot.compare) {
          snapshot.compare.textContent = snapshot.compareText;
          snapshot.compare.hidden = snapshot.compareHidden;
        } else {
          this.root.querySelector('.nordan-sticky-add-to-cart__button-price-compare')?.remove();
        }
      }
      this.pricePreviewSnapshot = null;
      return;
    }
    const context = this.getQuantityContext();
    const preview = this.section.nordanPricePreview?.getPreview(this.root.dataset.productId);
    if (!context || !preview) return;
    const current = this.root.querySelector('.nordan-sticky-add-to-cart__button-price-current');
    const compare = this.root.querySelector('.nordan-sticky-add-to-cart__button-price-compare');
    if (current && !this.pricePreviewSnapshot) {
      this.pricePreviewSnapshot = { current, text: current.textContent, compare, compareText: compare?.textContent, compareHidden: compare?.hidden };
    }
    this.syncPrices(context, Number(context.quantityInput.value));
  }

  observeBundlePrices(details) {
    if (this.bundleDetails === details) return;
    this.bundlePriceObserver?.disconnect();
    this.bundleDetails = details;
    if (!details) return;
    this.bundlePriceObserver = new MutationObserver(() => this.scheduleSync());
    this.bundlePriceObserver.observe(details, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['data-n2-bundle-active', 'data-mode', 'data-selected', 'aria-busy', 'hidden'],
    });
  }

  restoreOriginalPriceDisplay() {
    const state = this.bundlePriceDisplay;
    if (!state) return;
    if (state.title === null) state.current.removeAttribute('title');
    else state.current.setAttribute('title', state.title);
    if (state.addedCompare) state.compare.remove();
    else state.compare.hidden = state.compareHidden;
    this.bundlePriceDisplay = null;
  }

  syncBundlePrice(context, quantity) {
    const bundle = this.bundleDetails?.querySelector('nordan-2-bundle-selector');
    const active = bundle && bundle.dataset.mode === 'bundle' && !bundle.hidden && !bundle.failed &&
      this.bundleDetails.getAttribute('data-n2-bundle-active') === bundle.dataset.blockId;
    if (!active) {
      this.restoreOriginalPriceDisplay();
      return false;
    }
    const current = this.root.querySelector('.nordan-sticky-add-to-cart__button-price-current');
    if (!current) return true;
    const preview = this.section.nordanPricePreview?.getPreview(this.root.dataset.productId);
    if (preview) {
      const quote = bundle.getPricePreview?.(preview.variant.id, quantity);
      if (quote) this.writePreviewPrice(quote.total, quote.original, !quote.compareHidden);
      return true;
    }
    let compare = this.root.querySelector('.nordan-sticky-add-to-cart__button-price-compare');
    if (this.bundlePriceDisplay?.current !== current || this.bundlePriceDisplay?.compare !== compare) {
      this.restoreOriginalPriceDisplay();
      const addedCompare = !compare;
      if (!compare) {
        compare = document.createElement('s');
        compare.className = 'nordan-sticky-add-to-cart__button-price-compare';
        current.parentElement.append(compare);
      }
      this.bundlePriceDisplay = { current, compare, addedCompare, compareHidden: compare.hidden, title: current.getAttribute('title') };
    }
    const variantId = context.productForm.querySelector('input[name="id"]')?.value;
    const selected = bundle.querySelector('[data-bundle-card][data-selected]');
    const total = selected?.querySelector('[data-bundle-total]');
    const original = selected?.querySelector('[data-bundle-original]');
    const ready = !bundle.busy && bundle.getAttribute('aria-busy') !== 'true' &&
      bundle.config?.available && String(bundle.config.productId) === this.root.dataset.productId &&
      String(bundle.config.variantId) === variantId && context.quantityInput.validity.valid &&
      !context.quantityInput.disabled && Number.isSafeInteger(quantity) && quantity > 0 &&
      Number(bundle.querySelector('[data-bundle-quantity]')?.textContent) === quantity &&
      selected?.querySelector('[data-bundle-tier]')?.getAttribute('aria-checked') === 'true' &&
      total?.textContent?.trim() && original?.textContent?.trim() &&
      Number.isSafeInteger(bundle.config.price * quantity) &&
      original.textContent.trim() === formatMoney(bundle.config.price * quantity, bundle.config.moneyFormat, bundle.config.currency);
    // Copy the selected Bundle quote; this is not an existing cart line or a checkout total.
    current.textContent = ready ? total.textContent.trim() : '—';
    current.title = ready
      ? 'Estimated total for this selection. Final discounts calculated in cart and checkout.'
      : 'Updating selection…';
    compare.hidden = !ready || original.hidden;
    compare.textContent = ready ? original.textContent.trim() : '';
    return true;
  }

  handleClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const decreaseButton = target.closest(NORDAN_STICKY_QUANTITY_DECREASE_SELECTOR);
    const increaseButton = target.closest(NORDAN_STICKY_QUANTITY_INCREASE_SELECTOR);
    const quantityButton = decreaseButton || increaseButton;
    if (!(quantityButton instanceof HTMLButtonElement) || !this.root.contains(quantityButton)) return;

    event.preventDefault();
    const context = this.getQuantityContext();
    if (!context || quantityButton.disabled) {
      this.syncControls(context);
      return;
    }

    context.quantitySelector.updateQuantity(decreaseButton ? -1 : 1);
    this.syncControls(context);
  }

  handleQuantityUpdate(event) {
    if (event.detail?.cartLine) return;

    const context = this.getQuantityContext();
    if (!context || event.target !== context.quantitySelector) return;
    this.syncControls(context);
  }

  handleProductSelect(event) {
    if (!(event.target instanceof Element) || !this.section.contains(event.target) || !event.promise) return;

    event.promise
      .then(() => {
        if (this.section.nordanPricePreview && !this.section.nordanPricePreview.isCurrentEvent(event)) return;
        this.pricePreviewSnapshot = null;
        this.scheduleSync();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') this.scheduleSync();
      });
  }

  destroy() {
    if (this.isDestroyed) return;
    this.isDestroyed = true;
    this.bundlePriceObserver?.disconnect();
    this.restoreOriginalPriceDisplay();
    this.root?.removeEventListener('click', this.boundHandleClick);
    this.section?.removeEventListener(NORDAN_QUANTITY_UPDATE_EVENT, this.boundHandleQuantityUpdate);
    this.section?.removeEventListener(NORDAN_PRODUCT_SELECT_EVENT, this.boundHandleProductSelect);
    this.section?.removeEventListener('nordan:price-preview', this.boundHandlePricePreview);
    this.section?.removeEventListener('nordan:bundle-quote', this.boundHandleBundleQuote);

    if (this.syncFrame !== null) {
      cancelAnimationFrame(this.syncFrame);
      this.syncFrame = null;
    }

    nordanStickyQuantityInstances.delete(this);
    nordanStickyQuantityByRoot.delete(this.root);
    this.root = null;
    this.section = null;
  }
}

function initNordanStickyQuantityControls() {
  document.querySelectorAll(NORDAN_STICKY_ADD_TO_CART_SELECTOR).forEach((root) => {
    if (!(root instanceof HTMLElement) || nordanStickyQuantityByRoot.has(root)) return;

    const controls = new NordanStickyQuantityControls(root);
    if (controls.isReady) {
      nordanStickyQuantityByRoot.set(root, controls);
      nordanStickyQuantityInstances.add(controls);
    }
  });
}

function destroyNordanStickyQuantityControls(event) {
  const sectionRoot = event.target instanceof Element ? event.target : null;

  nordanStickyQuantityInstances.forEach((controls) => {
    if (!controls.root?.isConnected || sectionRoot?.contains(controls.root)) controls.destroy();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNordanStickyQuantityControls);
} else {
  initNordanStickyQuantityControls();
}

document.addEventListener('shopify:section:load', initNordanStickyQuantityControls);
document.addEventListener('shopify:section:unload', destroyNordanStickyQuantityControls);

const NORDAN_STICKY_TRUE_FOOTER_SELECTOR = 'footer .footer-content';
const NORDAN_STICKY_MOBILE_QUERY = '(max-width: 749px)';
const NORDAN_STICKY_GLOBAL_FOOTER_MARGIN = 200;
const nordanStickyFooterVisibilityByRoot = new WeakMap();
const nordanStickyFooterVisibilityInstances = new Set();

class NordanStickyFooterVisibility {
  constructor(root) {
    this.root = root;
    this.section = root.closest('.shopify-section');
    this.stickyBar = root.querySelector('[ref="stickyBar"]');
    this.productForm = null;
    this.buyButtonsBlock = null;
    this.footer = null;
    this.trueFooter = null;
    this.mobileQuery = window.matchMedia(NORDAN_STICKY_MOBILE_QUERY);
    this.buyButtonsObserver = null;
    this.footerObserver = null;
    this.updateFrame = null;
    this.footerRefreshFrame = null;
    this.isDestroyed = false;
    this.boundScheduleUpdate = this.scheduleUpdate.bind(this);
    this.boundRefreshFooterTargets = this.refreshFooterTargets.bind(this);
    this.boundHandleProductSelect = this.handleProductSelect.bind(this);

    const productId = root.dataset.productId;
    if (this.section instanceof HTMLElement && productId) {
      this.productForm = this.section.querySelector(`product-form-component[data-product-id="${productId}"]`);
      this.buyButtonsBlock = this.productForm?.closest('.buy-buttons-block');
    }

    this.isReady =
      this.section instanceof HTMLElement &&
      this.stickyBar instanceof HTMLElement &&
      this.buyButtonsBlock instanceof HTMLElement;

    if (!this.isReady) return;

    this.buyButtonsObserver = new IntersectionObserver(this.boundScheduleUpdate);
    this.buyButtonsObserver.observe(this.buyButtonsBlock);
    this.mobileQuery.addEventListener('change', this.boundRefreshFooterTargets);
    this.section.addEventListener(NORDAN_PRODUCT_SELECT_EVENT, this.boundHandleProductSelect);

    customElements.whenDefined('shopify-chat').then(() => this.scheduleUpdate());
    this.refreshFooterTargets();
  }

  isChatActive() {
    if (!customElements.get('shopify-chat')) return false;

    const chat = document.querySelector('shopify-chat');
    if (!(chat instanceof HTMLElement)) return false;

    return chat.hasAttribute('open');
  }

  isElementInViewport(element, topMargin = 0) {
    if (!(element instanceof HTMLElement)) return false;

    const rect = element.getBoundingClientRect();
    return rect.bottom > -topMargin && rect.top < window.innerHeight;
  }

  clearFooterTargets() {
    if (this.footerRefreshFrame !== null) {
      cancelAnimationFrame(this.footerRefreshFrame);
      this.footerRefreshFrame = null;
    }

    this.footerObserver?.disconnect();
    this.footerObserver = null;
    this.footer = null;
    this.trueFooter = null;
  }

  refreshFooterTargets() {
    if (this.isDestroyed || !this.root?.isConnected) return;

    this.clearFooterTargets();

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    const trueFooter = document.querySelector(NORDAN_STICKY_TRUE_FOOTER_SELECTOR);
    this.footer = footer instanceof HTMLElement ? footer : null;
    this.trueFooter = trueFooter instanceof HTMLElement ? trueFooter : null;

    const footerTarget = this.mobileQuery.matches ? this.trueFooter : this.footer;
    if (footerTarget instanceof HTMLElement) {
      this.footerObserver = new IntersectionObserver(
        this.boundScheduleUpdate,
        this.mobileQuery.matches
          ? undefined
          : { rootMargin: `${NORDAN_STICKY_GLOBAL_FOOTER_MARGIN}px 0px 0px 0px` }
      );
      this.footerObserver.observe(footerTarget);
    }

    this.scheduleUpdate();
  }

  scheduleFooterTargetsRefresh() {
    if (this.isDestroyed || this.footerRefreshFrame !== null) return;

    this.footerRefreshFrame = requestAnimationFrame(() => {
      this.footerRefreshFrame = null;
      this.refreshFooterTargets();
    });
  }

  scheduleUpdate() {
    if (this.isDestroyed || this.updateFrame !== null) return;

    this.updateFrame = requestAnimationFrame(() => {
      this.updateFrame = null;
      if (!this.isDestroyed) this.updateVisibility();
    });
  }

  updateVisibility() {
    if (
      !this.root?.isConnected ||
      !this.stickyBar?.isConnected ||
      !this.buyButtonsBlock?.isConnected
    ) {
      return;
    }

    const footerTarget = this.mobileQuery.matches ? this.trueFooter : this.footer;
    if (!(footerTarget instanceof HTMLElement)) return;
    if (!footerTarget.isConnected) {
      this.scheduleFooterTargetsRefresh();
      return;
    }

    const buyButtonsHavePassed = this.buyButtonsBlock.getBoundingClientRect().bottom < 0;
    const footerIsVisible = this.isElementInViewport(
      footerTarget,
      this.mobileQuery.matches ? 0 : NORDAN_STICKY_GLOBAL_FOOTER_MARGIN
    );
    const shouldShow =
      buyButtonsHavePassed &&
      !footerIsVisible &&
      !this.isChatActive() &&
      this.root.dataset.variantAvailable === 'true';

    this.stickyBar.dataset.stuck = shouldShow ? 'true' : 'false';
  }

  handleProductSelect(event) {
    if (!(event.target instanceof Element) || !this.section.contains(event.target) || !event.promise) return;

    event.promise
      .then(() => this.scheduleUpdate())
      .catch((error) => {
        if (error?.name !== 'AbortError') this.scheduleUpdate();
      });
  }

  destroy() {
    if (this.isDestroyed) return;
    this.isDestroyed = true;
    this.buyButtonsObserver?.disconnect();
    this.clearFooterTargets();
    this.mobileQuery?.removeEventListener('change', this.boundRefreshFooterTargets);
    this.section?.removeEventListener(NORDAN_PRODUCT_SELECT_EVENT, this.boundHandleProductSelect);

    if (this.updateFrame !== null) {
      cancelAnimationFrame(this.updateFrame);
      this.updateFrame = null;
    }

    if (this.footerRefreshFrame !== null) {
      cancelAnimationFrame(this.footerRefreshFrame);
      this.footerRefreshFrame = null;
    }

    nordanStickyFooterVisibilityInstances.delete(this);
    nordanStickyFooterVisibilityByRoot.delete(this.root);
    this.root = null;
    this.section = null;
    this.stickyBar = null;
    this.productForm = null;
    this.buyButtonsBlock = null;
    this.footer = null;
    this.trueFooter = null;
    this.mobileQuery = null;
  }
}

function initNordanStickyFooterVisibility(scope = document) {
  scope.querySelectorAll(NORDAN_STICKY_ADD_TO_CART_SELECTOR).forEach((root) => {
    if (!(root instanceof HTMLElement) || nordanStickyFooterVisibilityByRoot.has(root)) return;

    const visibility = new NordanStickyFooterVisibility(root);
    if (visibility.isReady) {
      nordanStickyFooterVisibilityByRoot.set(root, visibility);
      nordanStickyFooterVisibilityInstances.add(visibility);
    }
  });
}

function isNordanTrueFooterSection(sectionRoot) {
  if (!(sectionRoot instanceof Element)) return false;
  return sectionRoot.matches('.footer-content') || sectionRoot.querySelector('.footer-content') instanceof HTMLElement;
}

function destroyNordanStickyFooterVisibility(event) {
  const sectionRoot = event.target instanceof Element ? event.target : null;
  const isTrueFooterSection = isNordanTrueFooterSection(sectionRoot);

  nordanStickyFooterVisibilityInstances.forEach((visibility) => {
    if (!visibility.root?.isConnected || sectionRoot?.contains(visibility.root)) {
      visibility.destroy();
      return;
    }

    if (isTrueFooterSection) {
      visibility.clearFooterTargets();
    }
  });
}

function refreshNordanStickyFooterTargets() {
  nordanStickyFooterVisibilityInstances.forEach((visibility) => {
    if (visibility.root?.isConnected) visibility.refreshFooterTargets();
    else visibility.destroy();
  });
}

function handleNordanStickyFooterSectionLoad(event) {
  const sectionRoot = event.target instanceof Element ? event.target : null;
  if (!sectionRoot) return;

  if (isNordanTrueFooterSection(sectionRoot)) {
    refreshNordanStickyFooterTargets();
    return;
  }

  initNordanStickyFooterVisibility(sectionRoot);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNordanStickyFooterVisibility);
} else {
  initNordanStickyFooterVisibility();
}

document.addEventListener('shopify:section:load', handleNordanStickyFooterSectionLoad);
document.addEventListener('shopify:section:unload', destroyNordanStickyFooterVisibility);

const NORDAN_BOX_CALCULATOR_SELECTOR = '[data-nordan-quantity-coverage]';
const NORDAN_BOX_CALCULATOR_OPEN_SELECTOR = '[data-nordan-box-calculator-open]';
const NORDAN_STICKY_BOX_CALCULATOR_OPEN_SELECTOR = '[data-nordan-sticky-box-calculator-open]';
const NORDAN_BOX_CALCULATOR_CLOSE_SELECTOR = '[data-nordan-box-calculator-close]';
const NORDAN_BOX_CALCULATOR_FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'a[href]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');
let activeNordanBoxCalculator = null;
const nordanBoxCalculatorInstances = new Set();
const nordanBoxCalculatorByRoot = new WeakMap();

class NordanBoxCalculator {
  constructor(root) {
    this.root = root;
    this.grid = root.closest('.product-information__grid');
    if (!this.grid?.querySelector('.nordan-gallery')) this.grid = null;

    this.section = root.closest('.shopify-section') || this.grid || root;
    this.variantPicker =
      this.grid?.querySelector('.product-details variant-picker') ||
      this.section.querySelector('.product-details variant-picker');
    this.triggers = Array.from(root.querySelectorAll(NORDAN_BOX_CALCULATOR_OPEN_SELECTOR));
    this.trigger = this.triggers[0];
    this.activeTrigger = null;
    this.modal = root.querySelector('[data-nordan-box-calculator-modal]');
    this.dialog = root.querySelector('[data-nordan-box-calculator-dialog]');
    this.widthInput = root.querySelector('[data-nordan-box-calculator-width]');
    this.heightInput = root.querySelector('[data-nordan-box-calculator-height]');
    this.errorNode = root.querySelector('[data-nordan-box-calculator-error]');
    this.resultNode = root.querySelector('[data-nordan-box-calculator-result]');
    this.totalTextNode = root.querySelector('[data-nordan-box-calculator-total-text]');
    this.variantDetailsNode = root.querySelector('[data-nordan-box-calculator-variant-details]');
    this.panelNameNode = root.querySelector('[data-nordan-box-calculator-panel-name]');
    this.panelSizeNode = root.querySelector('[data-nordan-box-calculator-panel-size]');
    this.panelPackNode = root.querySelector('[data-nordan-box-calculator-panel-pack]');
    this.coverageNode = root.querySelector('[data-nordan-box-calculator-coverage]');
    this.calculateButton = root.querySelector('[data-nordan-box-calculator-calculate]');
    this.addButton = root.querySelector('[data-nordan-box-calculator-add]');
    this.resetButton = root.querySelector('[data-nordan-box-calculator-reset]');
    this.config = this.getConfig();
    this.boxesForThisWall = null;
    this.calculatedVariantId = null;
    this.variantUpdateGeneration = 0;
    this.variantUpdating = false;
    this.variantUpdateFailed = false;
    this.isReady = Boolean(
      this.trigger &&
        this.modal &&
        this.dialog &&
        this.widthInput &&
        this.heightInput &&
        this.calculateButton &&
        this.addButton &&
        this.config?.variants
    );

    if (!this.isReady) return;

    this.boundOpen = this.open.bind(this);
    this.boundHandleModalClick = this.handleModalClick.bind(this);
    this.boundHandleCalculate = this.handleCalculate.bind(this);
    this.boundHandleDimensionInput = this.handleDimensionInput.bind(this);
    this.boundHandleReset = this.handleReset.bind(this);
    this.boundHandleAdd = this.handleAdd.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.boundHandleProductSelect = this.handleProductSelect.bind(this);
    this.boundHandleStickyOpen = this.handleStickyOpen.bind(this);

    this.triggers.forEach((trigger) => trigger.addEventListener('click', this.boundOpen));
    this.modal.addEventListener('click', this.boundHandleModalClick);
    this.calculateButton.addEventListener('click', this.boundHandleCalculate);
    this.widthInput.addEventListener('input', this.boundHandleDimensionInput);
    this.heightInput.addEventListener('input', this.boundHandleDimensionInput);
    this.resetButton?.addEventListener('click', this.boundHandleReset);
    this.addButton.addEventListener('click', this.boundHandleAdd);
    this.section.addEventListener(NORDAN_PRODUCT_SELECT_EVENT, this.boundHandleProductSelect);
    this.section.addEventListener('click', this.boundHandleStickyOpen);
  }

  getConfig() {
    const configScript = this.section.querySelector('[data-nordan-unit-price-config]');
    if (!configScript?.textContent) return null;

    try {
      const config = JSON.parse(configScript.textContent);
      return {
        variants: config.variants || {},
      };
    } catch (error) {
      console.warn('[nordan-box-calculator] Invalid coverage config.', error);
      return null;
    }
  }

  getPurchaseContext() {
    if (!this.root?.isConnected) return null;

    const quantityInput = this.root.querySelector('input[name="quantity"]');
    const quantitySelector = quantityInput?.closest('quantity-selector-component');
    const productForm = this.root.closest('product-form-component');
    const addToCartButton = productForm?.querySelector(
      'form[data-type="add-to-cart-form"] add-to-cart-component button[ref="addToCartButton"][type="submit"][name="add"]'
    );
    const variantInput = productForm?.querySelector('input[name="id"]');

    if (
      !(quantityInput instanceof HTMLInputElement) ||
        !(quantitySelector instanceof HTMLElement) ||
        !(productForm instanceof HTMLElement) ||
        !(addToCartButton instanceof HTMLButtonElement) ||
        !(variantInput instanceof HTMLInputElement) ||
      typeof quantitySelector.getCurrentValues !== 'function' ||
      typeof quantitySelector.updateQuantity !== 'function'
    ) {
      return null;
    }

    return { quantityInput, quantitySelector, productForm, addToCartButton, variantInput };
  }

  getCoverageByVariantId(variantId) {
    if (!variantId) return null;
    const variantConfig = this.config.variants[String(variantId)];
    const coverage = Number(variantConfig?.coverageSqFt);

    return Number.isFinite(coverage) && coverage > 0 ? coverage : null;
  }

  getVariantFromScript() {
    const variantScript = this.variantPicker?.querySelector('script[type="application/json"]');
    if (!variantScript?.textContent) return null;

    try {
      return JSON.parse(variantScript.textContent);
    } catch (error) {
      console.warn('[nordan-box-calculator] Invalid selected variant JSON.', error);
      return null;
    }
  }

  getCurrentVariantContext() {
    if (this.variantUpdating || this.variantUpdateFailed) return null;

    const variantId = this.getVariantFromScript()?.id;
    const coverage = this.getCoverageByVariantId(variantId);
    if (!coverage || !variantId) return null;

    return {
      variantId: String(variantId),
      coverage,
    };
  }

  getSelectedPanelDisplay() {
    const selectedOption = this.variantPicker?.querySelector('.nordan-panel-size-card__input:checked');
    const card = selectedOption?.closest('.nordan-panel-size-card');
    const detailNodes = Array.from(card?.querySelectorAll('.nordan-panel-size-card__size') || []);
    const name = card?.querySelector('.nordan-panel-size-card__title')?.textContent?.trim() || '';
    const size = detailNodes[0]?.textContent?.trim() || '';
    const pack = detailNodes[1]?.textContent?.trim() || '';

    return { name, size, pack };
  }

  updateVariantDetails(context = this.getCurrentVariantContext()) {
    const panelDisplay = this.getSelectedPanelDisplay();
    const coverage = Number(context?.coverage);
    if (!panelDisplay.name || !Number.isFinite(coverage) || coverage <= 0) {
      this.clearVariantDetails();
      return;
    }

    if (this.panelNameNode) this.panelNameNode.textContent = panelDisplay.name;
    if (this.panelSizeNode) this.panelSizeNode.textContent = panelDisplay.size;
    if (this.panelPackNode) this.panelPackNode.textContent = panelDisplay.pack;
    if (this.coverageNode) this.coverageNode.textContent = coverage.toFixed(2);
    if (this.variantDetailsNode) this.variantDetailsNode.hidden = false;
  }

  clearVariantDetails() {
    if (this.panelNameNode) this.panelNameNode.textContent = '';
    if (this.panelSizeNode) this.panelSizeNode.textContent = '';
    if (this.panelPackNode) this.panelPackNode.textContent = '';
    if (this.coverageNode) this.coverageNode.textContent = '';
    if (this.variantDetailsNode) this.variantDetailsNode.hidden = true;
  }

  parseDimension(input) {
    const value = Number(String(input?.value || '').replace(',', '.'));

    return Number.isFinite(value) && value > 0 ? value : null;
  }

  getCartDrawer() {
    const drawer = document.querySelector('theme-drawer#cart-drawer');

    return drawer instanceof HTMLElement && typeof drawer.close === 'function' ? drawer : null;
  }

  async open(event, trigger = event?.currentTarget) {
    event?.preventDefault();
    this.activeTrigger = trigger instanceof HTMLElement ? trigger : this.trigger;
    if (!this.modal?.hidden) {
      this.widthInput?.focus({ preventScroll: true });
      return;
    }

    const cartDrawer = this.getCartDrawer();
    if (cartDrawer?.hasAttribute('open')) {
      await cartDrawer.close();
      requestAnimationFrame(() => this.openCalculator());
      return;
    }

    this.openCalculator();
  }

  openCalculator() {
    this.clearQuantityFeedback();
    if (!this.root?.isConnected || !this.modal?.hidden) return;

    if (activeNordanBoxCalculator && activeNordanBoxCalculator !== this) {
      activeNordanBoxCalculator.close(false);
    }

    activeNordanBoxCalculator = this;
    this.modal.hidden = false;
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
    document.documentElement.classList.add('nordan-box-calculator-open');
    document.addEventListener('keydown', this.boundHandleKeydown);
    this.invalidateResult();
    this.clearError();
    this.updateCoverageAvailability();
    this.widthInput?.focus({ preventScroll: true });
  }

  close(restoreFocus = true) {
    if (!this.modal || this.modal.hidden) return;

    this.modal.hidden = true;
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    document.removeEventListener('keydown', this.boundHandleKeydown);

    if (activeNordanBoxCalculator === this) {
      activeNordanBoxCalculator = null;
      document.documentElement.classList.remove('nordan-box-calculator-open');
    }

    const focusTrigger = this.activeTrigger?.isConnected
      ? this.activeTrigger
      : this.triggers.find((trigger) => trigger.isConnected);
    if (restoreFocus && focusTrigger) {
      focusTrigger.focus({ preventScroll: true });
    }
    this.activeTrigger = null;
  }

  destroy() {
    this.clearQuantityFeedback();
    this.triggers.forEach((trigger) => trigger.removeEventListener('click', this.boundOpen));
    this.modal?.removeEventListener('click', this.boundHandleModalClick);
    this.calculateButton?.removeEventListener('click', this.boundHandleCalculate);
    this.widthInput?.removeEventListener('input', this.boundHandleDimensionInput);
    this.heightInput?.removeEventListener('input', this.boundHandleDimensionInput);
    this.resetButton?.removeEventListener('click', this.boundHandleReset);
    this.addButton?.removeEventListener('click', this.boundHandleAdd);
    this.section?.removeEventListener(NORDAN_PRODUCT_SELECT_EVENT, this.boundHandleProductSelect);
    this.section?.removeEventListener('click', this.boundHandleStickyOpen);
    document.removeEventListener('keydown', this.boundHandleKeydown);

    if (activeNordanBoxCalculator === this) {
      this.close(false);
      activeNordanBoxCalculator = null;
      document.documentElement.classList.remove('nordan-box-calculator-open');
    }
    this.activeTrigger = null;

    this.root?.removeAttribute('data-nordan-box-calculator-initialized');
    nordanBoxCalculatorInstances.delete(this);
    nordanBoxCalculatorByRoot.delete(this.root);
  }

  handleStickyOpen(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const stickyTrigger = target.closest(NORDAN_STICKY_BOX_CALCULATOR_OPEN_SELECTOR);
    if (!stickyTrigger || !this.section?.contains(stickyTrigger)) return;

    const primaryCalculatorTrigger = this.section.querySelector(NORDAN_BOX_CALCULATOR_OPEN_SELECTOR);
    if (primaryCalculatorTrigger !== this.trigger) return;

    event.preventDefault();
    this.open(event, stickyTrigger);
  }

  handleModalClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest(NORDAN_BOX_CALCULATOR_CLOSE_SELECTOR)) {
      event.preventDefault();
      this.close();
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = this.getFocusableElements();
    if (!focusableElements.length) {
      event.preventDefault();
      this.dialog?.focus({ preventScroll: true });
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;

    if (!this.dialog?.contains(activeElement)) {
      event.preventDefault();
      (event.shiftKey ? lastElement : firstElement).focus({ preventScroll: true });
    } else if (event.shiftKey && activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus({ preventScroll: true });
    } else if (!event.shiftKey && activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus({ preventScroll: true });
    }
  }

  getFocusableElements() {
    if (!this.dialog) return [];

    return Array.from(this.dialog.querySelectorAll(NORDAN_BOX_CALCULATOR_FOCUSABLE_SELECTOR)).filter((element) => {
      return element instanceof HTMLElement && !element.hidden && element.getClientRects().length > 0;
    });
  }

  handleCalculate(event) {
    event.preventDefault();
    this.invalidateResult();
    this.clearError();
    if (this.calculate()) {
      this.root.dispatchEvent(new CustomEvent('nordan:calculator:result', {
        bubbles: true,
        detail: {
          productId: this.root.closest('product-form-component')?.dataset.productId,
          variantId: this.calculatedVariantId,
          boxes: this.boxesForThisWall,
        },
      }));
    }
  }

  handleDimensionInput() {
    this.invalidateResult();
    this.clearError();
  }

  handleReset() {
    if (this.widthInput) this.widthInput.value = '';
    if (this.heightInput) this.heightInput.value = '';
    this.invalidateResult();
    this.clearError();
    this.widthInput?.focus({ preventScroll: true });
  }

  // handleAdd() {
  //   if (!this.boxesForThisWall || !this.calculatedVariantId) return;

  //   const purchaseContext = this.getPurchaseContext();
  //   if (!purchaseContext) {
  //     this.showError('The product form is updating. Please calculate again.');
  //     return;
  //   }

  //   const { quantityInput, quantitySelector, addToCartButton, variantInput } = purchaseContext;
  //   if (quantityInput.disabled) {
  //     this.showError(
  //       this.modal?.dataset.quantityUnavailableMessage || 'Quantity is not available for the selected option.'
  //     );
  //     return;
  //   }

  //   if (!addToCartButton.isConnected || addToCartButton.disabled) {
  //     this.showError('The selected option cannot currently be added to cart.');
  //     return;
  //   }

  //   const currentContext = this.getCurrentVariantContext();
  //   if (
  //     !currentContext ||
  //     currentContext.variantId !== this.calculatedVariantId ||
  //     variantInput.value !== currentContext.variantId
  //   ) {
  //     this.invalidateResult();
  //     this.showError('The selected option changed. Please calculate again.');
  //     return;
  //   }

  //   const { value, step } = quantitySelector.getCurrentValues();
  //   const increment = Number.isFinite(step) && step > 0 ? step : 1;
  //   const stepMultiplier = (this.boxesForThisWall - value) / increment;
  //   if (!Number.isInteger(stepMultiplier)) {
  //     this.showError(
  //       this.modal?.dataset.quantityRulesChangedMessage || 'The purchase quantity rules changed. Please calculate again.'
  //     );
  //     return;
  //   }

  //   try {
  //     quantitySelector.updateQuantity(stepMultiplier);
  //   } catch (error) {
  //     console.warn('[nordan-box-calculator] Could not update quantity.', error);
  //     this.showError('The product form is updating. Please calculate again.');
  //     return;
  //   }

  //   const actualQuantity = Number(quantityInput.value);
  //   if (!Number.isFinite(actualQuantity) || actualQuantity <= 0) {
  //     this.showError(
  //       this.modal?.dataset.quantityUnavailableMessage || 'Quantity is not available for the selected option.'
  //     );
  //     return;
  //   }

  //   this.clearError();
  //   addToCartButton.click();
  //   this.close(false);


  // }

handleAdd() {
  if (!this.boxesForThisWall || !this.calculatedVariantId) {
    this.showError('Please calculate first.');
    return;
  }

  let purchaseContext = this.getPurchaseContext();
  if (!purchaseContext) {
    this.showError('The product form is updating. Please calculate again.');
    return;
  }

  let { quantityInput, quantitySelector, addToCartButton, variantInput } = purchaseContext;

  if (quantityInput.disabled) {
    this.showError(
      this.modal?.dataset.quantityUnavailableMessage || 'Quantity is not available for the selected option.'
    );
    return;
  }

  if (!addToCartButton.isConnected || addToCartButton.disabled) {
    this.showError('The selected option cannot currently be added to cart.');
    return;
  }

  // 获取当前变体上下文
  let currentContext = this.getCurrentVariantContext();
  if (!currentContext) {
    this.showError('Could not get current variant information.');
    return;
  }

  // 如果变体不匹配，尝试自动重新计算
  if (currentContext.variantId !== this.calculatedVariantId) {
    const width = this.parseDimension(this.widthInput);
    const height = this.parseDimension(this.heightInput);
    if (!width || !height) {
      this.showError('Please enter wall dimensions and calculate again.');
      return;
    }
    // 重新计算
    this.calculate();
    // 重新获取当前变体上下文
    currentContext = this.getCurrentVariantContext();
    if (!currentContext || currentContext.variantId !== this.calculatedVariantId) {
      this.showError('Could not calculate quantity for the selected option. Please try again.');
      return;
    }
    // 重新获取购买上下文（因为变体更新后，相关元素可能刷新）
    purchaseContext = this.getPurchaseContext();
    if (!purchaseContext) {
      this.showError('The product form is updating. Please calculate again.');
      return;
    }
    // 解构新的上下文
    ({ quantityInput, quantitySelector, addToCartButton, variantInput } = purchaseContext);
    // 再次检查按钮状态（可能已变化）
    if (quantityInput.disabled || !addToCartButton.isConnected || addToCartButton.disabled) {
      this.showError('The selected option cannot currently be added to cart.');
      return;
    }
    // 更新 currentContext（已在上面的 if 中获取）
  }

  // 现在变体匹配，继续回填数量
  // 再次检查变体是否匹配（安全校验）
  if (currentContext.variantId !== this.calculatedVariantId) {
    this.invalidateResult();
    this.showError('The selected option changed. Please calculate again.');
    return;
  }

  const { value, step } = quantitySelector.getCurrentValues();
  const increment = Number.isFinite(step) && step > 0 ? step : 1;
  const stepMultiplier = (this.boxesForThisWall - value) / increment;
  if (!Number.isInteger(stepMultiplier)) {
    this.showError(
      this.modal?.dataset.quantityRulesChangedMessage || 'The purchase quantity rules changed. Please calculate again.'
    );
    return;
  }

  try {
    quantitySelector.updateQuantity(stepMultiplier);
  } catch (error) {
    console.warn('[nordan-box-calculator] Could not update quantity.', error);
    this.showError('The product form is updating. Please calculate again.');
    return;
  }

  const actualQuantity = Number(quantityInput.value);
  if (!Number.isSafeInteger(actualQuantity) || actualQuantity <= 0) {
    this.showError(
      this.modal?.dataset.quantityUnavailableMessage || 'Quantity is not available for the selected option.'
    );
    return;
  }

  if (actualQuantity !== this.boxesForThisWall) {
    this.showError(
      'The recommended quantity exceeds the quantity currently available. Please check the available quantity and try again.'
    );
    return;
  }

  this.clearError();
  this.close(false);
  this.showAppliedQuantity(purchaseContext);
}


  // handleProductSelect(event) {
  //   const eventRoot = this.grid || this.section;
  //   if (!(event.target instanceof Element) || !eventRoot.contains(event.target) || !event.promise) return;

  //   const generation = ++this.variantUpdateGeneration;
  //   this.variantUpdating = true;
  //   this.variantUpdateFailed = false;
  //   this.clearVariantDetails();
  //   this.invalidateResult();
  //   this.updateCoverageAvailability();

  //   event.promise.then(
  //     () => {
  //       this.finishVariantUpdate(generation);
  //     },
  //     (error) => {
  //       if (error?.name !== 'AbortError') {
  //         this.finishVariantUpdate(generation);
  //       }
  //     }
  //   );
  // }

  handleProductSelect(event) {
    const eventRoot = this.grid || this.section;
    if (!(event.target instanceof Element) || !eventRoot.contains(event.target) || !event.promise) return;

    const generation = ++this.variantUpdateGeneration;
    this.variantUpdating = true;
    this.variantUpdateFailed = false;
    this.clearVariantDetails();
    this.invalidateResult();
    this.updateCoverageAvailability();

    event.promise.then(
      () => {
        if (!this.finishVariantUpdate(generation)) return;
        // ✅ 新增：变体更新完成后，如果有宽高输入，自动重新计算
        const width = this.parseDimension(this.widthInput);
        const height = this.parseDimension(this.heightInput);
        if (width && height && this.modal && !this.modal.hidden) {
          // 弹窗打开且有宽高 → 自动重新计算
          this.calculate();
          // 显示提示信息（可选）
          // this.showError('Variant changed. Results updated.', 'updating');
          // setTimeout(() => this.clearError(), 2000);
        }
      },
      (error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[nordan-box-calculator] Could not update variant.', error);
        }
        this.failVariantUpdate(generation);
      }
    );
  }

  finishVariantUpdate(generation) {
    // if (generation !== this.variantUpdateGeneration) return;

    // this.variantUpdating = false;
    // this.variantUpdateFailed = false;
    // if (!this.refreshAfterVariantUpdate()) return;

    // this.invalidateResult();
    // this.updateCoverageAvailability();
    if (generation !== this.variantUpdateGeneration) return false;
    this.variantUpdating = false;
    this.variantUpdateFailed = false;
    // ✅ 无论 refreshAfterVariantUpdate 结果如何，先清空旧结果
    this.invalidateResult();
    if (!this.refreshAfterVariantUpdate()) return false;
    this.updateCoverageAvailability();
    return true;
  }

  failVariantUpdate(generation) {
    if (generation !== this.variantUpdateGeneration) return;

    this.variantUpdating = false;
    this.variantUpdateFailed = true;
    this.clearVariantDetails();
    this.invalidateResult();
    this.calculateButton.disabled = true;
    this.showError(
      this.modal?.dataset.variantUpdateFailedMessage ||
        'We could not update the selected option. Please try again.'
    );
  }

  refreshAfterVariantUpdate() {
    if (!this.root?.isConnected) {
      this.destroy();
      initNordanBoxCalculators();
      return false;
    }

    const nextTriggers = Array.from(this.root.querySelectorAll(NORDAN_BOX_CALCULATOR_OPEN_SELECTOR));
    const nextModal = this.root.querySelector('[data-nordan-box-calculator-modal]');
    const nextDialog = this.root.querySelector('[data-nordan-box-calculator-dialog]');
    const nextWidthInput = this.root.querySelector('[data-nordan-box-calculator-width]');
    const nextHeightInput = this.root.querySelector('[data-nordan-box-calculator-height]');

    if (
      nextTriggers.length !== this.triggers.length ||
      nextTriggers.some((trigger, index) => trigger !== this.triggers[index]) ||
      nextModal !== this.modal ||
      nextDialog !== this.dialog ||
      nextWidthInput !== this.widthInput ||
      nextHeightInput !== this.heightInput
    ) {
      this.destroy();
      initNordanBoxCalculators();
      return false;
    }

    this.variantPicker =
      this.grid?.querySelector('.product-details variant-picker') ||
      this.section.querySelector('.product-details variant-picker');
    this.config = this.getConfig();
    this.root.dataset.nordanBoxCalculatorInitialized = 'true';
    return true;
  }

  updateCoverageAvailability() {
    if (this.variantUpdating) {
      this.clearVariantDetails();
      this.calculateButton.disabled = true;
      this.showError(this.modal?.dataset.variantUpdatingMessage || 'Updating selected option...', 'updating');
      return false;
    }

    if (this.variantUpdateFailed) {
      this.clearVariantDetails();
      this.calculateButton.disabled = true;
      this.showError(
        this.modal?.dataset.variantUpdateFailedMessage ||
          'We could not update the selected option. Please try again.'
      );
      return false;
    }

    const context = this.getCurrentVariantContext();
    const hasCoverage = Number.isFinite(context?.coverage) && context.coverage > 0;
    this.calculateButton.disabled = !hasCoverage;

    if (!hasCoverage) {
      this.clearVariantDetails();
      this.showError(this.modal?.dataset.coverageMissingMessage || 'Coverage data is not available for the selected option.');
      this.hideResult();
      return false;
    }

    this.updateVariantDetails(context);
    this.clearError();
    return true;
  }

  calculate() {
    if (this.variantUpdating || this.variantUpdateFailed) {
      this.updateCoverageAvailability();
      return false;
    }

    const context = this.getCurrentVariantContext();
    if (!Number.isFinite(context?.coverage) || context.coverage <= 0) {
      this.boxesForThisWall = null;
      this.calculatedVariantId = null;
      this.showError(this.modal?.dataset.coverageMissingMessage || 'Coverage data is not available for the selected option.');
      this.hideResult();
      this.updateAddButton();
      return false;
    }

    this.updateVariantDetails(context);

    const width = this.parseDimension(this.widthInput);
    const height = this.parseDimension(this.heightInput);
    if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
      this.boxesForThisWall = null;
      this.calculatedVariantId = null;
      this.showError(this.modal?.dataset.invalidMessage || 'Enter a wall width and height greater than 0.');
      this.hideResult();
      this.updateAddButton();
      return false;
    }

    // this.clearError();
    // this.boxesForThisWall = Math.max(1, Math.ceil((width * height) / context.coverage));
    // this.calculatedVariantId = context.variantId;
    // this.updateResultSummary();
    // if (this.resultNode) this.resultNode.hidden = false;
    // this.updateAddButton();
    // return true;
    this.clearError();
    const calculation = calculateNordanBoxRequirement({
      widthIn: width,
      heightIn: height,
      coverageSqFt: context.coverage,
    });

    if (!calculation.valid) {
      this.boxesForThisWall = null;
      this.calculatedVariantId = null;
      this.showError(this.modal?.dataset.invalidMessage || 'Enter a valid wall width and height.');
      this.hideResult();
      this.updateAddButton();
      return false;
    }

    this.boxesForThisWall = calculation.boxes;
    this.calculatedVariantId = context.variantId;
    this.updateResultSummary();
    if (this.resultNode) this.resultNode.hidden = false;
    this.updateAddButton();
    return true;
  }

  showAppliedQuantity({ quantitySelector, addToCartButton, productForm }) {
    this.clearQuantityFeedback();
    // Let the existing quantity listener select the card and restore the calculator trigger's focus first.
    this.quantityFeedbackFrame = requestAnimationFrame(() => {
      this.quantityFeedbackFrame = null;
      if (!this.root.isConnected || !this.modal.hidden || !addToCartButton.isConnected || addToCartButton.disabled) return;
      const details = productForm.closest('.product-details');
      const bundle = Array.from(details?.querySelectorAll('nordan-2-bundle-selector') || []).find((element) =>
        element.dataset.mode === 'bundle' && element.dataset.template === 'nordan' &&
        !element.hidden && !element.failed && element.boundForm === productForm &&
        details.getAttribute('data-n2-bundle-active') === element.dataset.blockId &&
        element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden'
      );
      const target = bundle ? bundle.querySelector('[data-bundle-card][data-selected]') : addToCartButton;
      if (!target) return;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const controller = new AbortController();
      this.quantityFeedbackController = controller;
      const finish = () => {
        if (controller.signal.aborted) return;
        controller.abort();
        window.clearTimeout(this.quantityScrollTimer);
        if (!target.isConnected || !this.modal.hidden || addToCartButton.disabled) return;
        // Bundle owns focus restoration; only the native-controls fallback moves focus here.
        if (!bundle) addToCartButton.focus({ preventScroll: true });
        if (reducedMotion) return;
        this.quantityFeedbackTargets = bundle ? [target] : [quantitySelector, addToCartButton];
        this.quantityFeedbackTargets.forEach((element) => element.classList.add('nordan-calculator-applied'));
        this.quantityFeedbackTimer = window.setTimeout(() => this.clearQuantityFeedback(), 1500);
      };
      const rect = target.getBoundingClientRect();
      const inView = rect.top >= 0 && rect.bottom <= window.innerHeight && rect.left >= 0 && rect.right <= window.innerWidth;
      if (inView) {
        finish();
        return;
      }
      if (!reducedMotion) {
        document.addEventListener('scrollend', finish, { once: true, signal: controller.signal });
        this.quantityScrollTimer = window.setTimeout(finish, 1000);
      }
      target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center', inline: 'nearest' });
      if (reducedMotion) finish();
    });
  }

  clearQuantityFeedback() {
    cancelAnimationFrame(this.quantityFeedbackFrame);
    this.quantityFeedbackFrame = null;
    this.quantityFeedbackController?.abort();
    window.clearTimeout(this.quantityScrollTimer);
    window.clearTimeout(this.quantityFeedbackTimer);
    this.quantityFeedbackTargets?.forEach((target) => target.classList.remove('nordan-calculator-applied'));
    this.quantityFeedbackController = null;
    this.quantityFeedbackTargets = [];
  }

  formatCountTemplate(template, count, fallback) {
    const value = String(count);
    const boxLabel = this.getBoxLabel(count);
    const pluralLabel = this.modal?.dataset.boxPlural?.trim() || 'boxes';
    const singularLabel = this.modal?.dataset.boxSingular?.trim() || 'box';
    let text = template?.trim() || fallback;

    if (text.includes('{count}')) {
      text = text.replaceAll('{count}', value);
    } else {
      text = `${text} (${value} ${boxLabel})`;
    }

    if (count === 1 && pluralLabel !== singularLabel) {
      text = text.replaceAll(pluralLabel, singularLabel).replace(/\bare\b/gi, 'is');
    }

    return text;
  }

  updateResultSummary() {
    if (this.totalTextNode) {
      this.totalTextNode.textContent = this.formatCountTemplate(
        this.modal?.dataset.totalNeededTemplate,
        this.boxesForThisWall,
        'You need {count} boxes for this wall'
      );
    }
  }

  updateAddButton() {
    if (!this.addButton) return;

    const hasBoxesToAdd = Number.isSafeInteger(this.boxesForThisWall) && this.boxesForThisWall > 0;
    const purchaseContext = this.getPurchaseContext();
    const variantContext = this.getCurrentVariantContext();

    const canAddBoxes =
      hasBoxesToAdd &&
      !this.variantUpdating &&
      !this.variantUpdateFailed &&
      variantContext?.variantId === this.calculatedVariantId &&
      Boolean(purchaseContext) &&
      !purchaseContext?.quantityInput.disabled &&
      !purchaseContext?.addToCartButton.disabled;
    const template = this.modal?.dataset.addButtonTemplate?.trim() || 'Apply {count} boxes';
    this.addButton.disabled = !canAddBoxes;
    this.addButton.textContent = this.formatCountTemplate(
      template,
      hasBoxesToAdd ? this.boxesForThisWall : 0,
      'Apply {count} boxes'
    );
  }

  getBoxLabel(count) {
    return count === 1
      ? this.modal?.dataset.boxSingular?.trim() || 'box'
      : this.modal?.dataset.boxPlural?.trim() || 'boxes';
  }

  showError(message, state = 'error') {
    if (!this.errorNode) return;

    this.errorNode.dataset.nordanBoxCalculatorState = state;
    this.errorNode.textContent = message;
    this.errorNode.hidden = false;
  }

  clearError() {
    if (!this.errorNode) return;

    this.errorNode.removeAttribute('data-nordan-box-calculator-state');
    this.errorNode.textContent = '';
    this.errorNode.hidden = true;
  }

  hideResult() {
    if (this.resultNode) this.resultNode.hidden = true;
    if (this.totalTextNode) this.totalTextNode.textContent = '';
  }

  invalidateResult() {
    this.boxesForThisWall = null;
    this.calculatedVariantId = null;
    this.hideResult();
    this.updateAddButton();
  }
}

function initNordanBoxCalculators() {
  document.querySelectorAll(NORDAN_BOX_CALCULATOR_SELECTOR).forEach((root) => {
    if (!(root instanceof HTMLElement) || nordanBoxCalculatorByRoot.has(root)) return;

    const calculator = new NordanBoxCalculator(root);
    if (calculator.isReady) {
      root.dataset.nordanBoxCalculatorInitialized = 'true';
      nordanBoxCalculatorByRoot.set(root, calculator);
      nordanBoxCalculatorInstances.add(calculator);
    }
  });
}

function destroyNordanBoxCalculators(event) {
  const sectionRoot = event.target instanceof Element ? event.target : null;
  const sectionId = event.detail?.sectionId;

  nordanBoxCalculatorInstances.forEach((calculator) => {
    const belongsToSection =
      !calculator.root?.isConnected ||
      sectionRoot?.contains(calculator.root) ||
      (sectionId && calculator.section?.id === `shopify-section-${sectionId}`);

    if (belongsToSection) calculator.destroy();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNordanBoxCalculators);
} else {
  initNordanBoxCalculators();
}

document.addEventListener('shopify:section:load', initNordanBoxCalculators);
document.addEventListener('shopify:section:unload', destroyNordanBoxCalculators);
const NORDAN_SIZE_HELP_OPEN_SELECTOR = '[data-nordan-size-help-open]';
const NORDAN_SIZE_HELP_CLOSE_SELECTOR = '[data-nordan-size-help-close]';
const NORDAN_SIZE_HELP_CONTENT_SELECTOR = '[data-nordan-size-help-content]';
const NORDAN_SIZE_HELP_ITEM_SOURCE_SELECTOR = '[data-nordan-size-help-item-source]';
let activeNordanSizeHelpModal = null;
let activeNordanSizeHelpTrigger = null;

function getNordanSizeHelpModalFromTrigger(trigger) {
  const modalId = trigger?.getAttribute?.('aria-controls');
  if (!modalId) return null;

  return document.getElementById(modalId);
}

function hydrateNordanSizeHelpModal(modal) {
  const content = modal.querySelector(NORDAN_SIZE_HELP_CONTENT_SELECTOR);
  if (!(content instanceof HTMLElement) || content.dataset.nordanSizeHelpHydrated === 'true') return;

  const sectionId = modal.dataset.nordanSizeHelpSectionId;
  const sources = Array.from(document.querySelectorAll(NORDAN_SIZE_HELP_ITEM_SOURCE_SELECTOR)).filter((source) => {
    return !sectionId || source.dataset.nordanSizeHelpSectionId === sectionId;
  });

  const fragment = document.createDocumentFragment();

  sources.forEach((source) => {
    if (source instanceof HTMLTemplateElement) {
      fragment.append(source.content.cloneNode(true));
      return;
    }

    source.childNodes.forEach((node) => {
      fragment.append(node.cloneNode(true));
    });
  });

  content.querySelectorAll('.nordan-size-help-modal__item, .nordan-size-help-modal__empty').forEach((item) => item.remove());
  content.append(fragment);
  content.dataset.nordanSizeHelpHydrated = 'true';
}

function openNordanSizeHelpModal(modal, trigger) {
  if (!(modal instanceof HTMLElement)) return;

  if (activeNordanSizeHelpModal && activeNordanSizeHelpModal !== modal) {
    closeNordanSizeHelpModal(activeNordanSizeHelpModal, false);
  }

  hydrateNordanSizeHelpModal(modal);
  activeNordanSizeHelpModal = modal;
  activeNordanSizeHelpTrigger = trigger instanceof HTMLElement ? trigger : null;
  modal.hidden = false;
  document.documentElement.classList.add('nordan-size-help-open');

  requestAnimationFrame(() => {
    modal.querySelector('[data-nordan-size-help-dialog]')?.focus({ preventScroll: true });
  });
}

function closeNordanSizeHelpModal(modal = activeNordanSizeHelpModal, restoreFocus = true) {
  if (!(modal instanceof HTMLElement)) return;

  modal.hidden = true;

  if (activeNordanSizeHelpModal === modal) {
    activeNordanSizeHelpModal = null;
    document.documentElement.classList.remove('nordan-size-help-open');

    if (restoreFocus && activeNordanSizeHelpTrigger?.isConnected) {
      activeNordanSizeHelpTrigger.focus({ preventScroll: true });
    }

    activeNordanSizeHelpTrigger = null;
  }
}

function handleNordanSizeHelpClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const openButton = target.closest(NORDAN_SIZE_HELP_OPEN_SELECTOR);
  if (openButton) {
    event.preventDefault();
    event.stopPropagation();
    openNordanSizeHelpModal(getNordanSizeHelpModalFromTrigger(openButton), openButton);
    return;
  }

  const closeButton = target.closest(NORDAN_SIZE_HELP_CLOSE_SELECTOR);
  if (closeButton) {
    event.preventDefault();
    closeNordanSizeHelpModal(closeButton.closest('[data-nordan-size-help-modal]'));
  }
}

function handleNordanSizeHelpKeydown(event) {
  if (event.key === 'Escape' && activeNordanSizeHelpModal) {
    event.preventDefault();
    closeNordanSizeHelpModal();
  }
}

document.addEventListener('click', handleNordanSizeHelpClick);
document.addEventListener('keydown', handleNordanSizeHelpKeydown);
